import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { EmailTemplate, EmailTemplateStyles } from '../../types/emailBuilder';
import { EmailBlock } from './blocks/EmailBlock';
import { BlockPalette } from './blocks/BlockPalette';
import { PropertiesPanel, FONT_FAMILIES } from './panels/PropertiesPanel';
import { GlobalSettingsPanel } from './panels/GlobalSettingsPanel';
import { BuilderLoadingScreen } from './BuilderLoadingScreen';
import { LayersPanel } from './LayersPanel';
import ComplianceFooter from './ComplianceFooter';
import { resolveComplianceFooterTextColor, COMPLIANCE_FOOTER_VARIANTS, ComplianceFooterVariant, normalizeComplianceFooterVariant } from './utils/complianceFooter';
import { generateEmailHTML } from './utils/emailHTMLGenerator';
import { FOOTER_BUILDER_CSS, FOOTER_BUILDER_MOBILE_CSS } from './utils/footerResponsiveCss';
import { generateId } from './utils/helpers';
import { createBlockFromType, createBlockFromContentFile } from './utils/blockFactory';
import { savedBlocksService } from '../../services/savedBlocksService';
import { recentBlocksService } from '../../services/recentBlocksService';
import { AIEmailGenerator } from './AIEmailGenerator';
import { useNotification } from '../../contexts/NotificationContext';
import EmailService from '../../services/emailService';
import { useAuth } from '../../contexts/AuthContext';
import { emailVerificationService, EmailVerification } from '../../services/emailVerificationService';
import { domainVerificationService, DomainVerification } from '../../services/domainVerificationService';
import { sendingDomainService } from '../../services/sendingDomainService';
import { CanvaImportGuideProvider } from '../../contexts/CanvaImportGuideContext';
import { parseEmailContainerWidthToPx } from '../../services/canvaConnectService';
import { emailPreviewService } from '../../services/emailPreviewService';

interface ModernEmailBuilderProps {
  template?: EmailTemplate;
  campaignName?: string;
  campaignId?: string;
  workspaceId?: string; // For poll tracking
  onSave: (template: EmailTemplate) => Promise<void> | void;
  onSaveAndNext?: (template: EmailTemplate) => Promise<void> | void;
  onCancel: () => void;
  onCampaignNameUpdate?: (name: string) => void;
  onManualSaveComplete?: () => Promise<void> | void;
  canRemoveBranding?: boolean; // Whether user can remove "Powered by Scubamail" badge (Pro/Business only)
}

export interface EmailBlockData {
  id: string;
  type: string;
  content: Record<string, any>;
  styles: Record<string, any>;
  /**
   * Optional mobile-specific style overrides (applied on canvas and exported HTML).
   * Only properties defined here override the base `styles` when rendering mobile previews.
   */
  mobileStyles?: Record<string, any>;
  isLocked?: boolean;
  isScubamailBranding?: boolean;
  isSignature?: boolean;
  hidden?: boolean;
  section?: 'header' | 'body' | 'footer';
  [key: string]: any;
}

const DEFAULT_STYLES: EmailTemplateStyles = {
  containerWidth: '600px',
  backgroundColor: '#ffffff',
  fontFamily: 'Arial, sans-serif',
  contentBackgroundColor: '#ffffff',
  padding: '0px',
  textColor: '#111827',
  showAddress: true,
  showBadge: true,
  complianceFooterVariant: 'classic',
};

const AUTOSAVE_DEBOUNCE_MS = 2000;
const MAX_HISTORY_ENTRIES = 50;

type BuilderHistoryState = {
  blocks: EmailBlockData[];
  templateName: string;
  templateSubject: string;
  styles: EmailTemplateStyles;
};

const cloneBlocks = (input: EmailBlockData[]): EmailBlockData[] => {
  return JSON.parse(JSON.stringify(input));
};

const MAX_DESIGN_PARSE_DEPTH = 4;

const normalizeDesignJson = (designJson: unknown): any => {
  if (!designJson) {
    return null;
  }
  if (typeof designJson === 'string') {
    try {
      return JSON.parse(designJson);
    } catch (err) {
      console.warn('ModernEmailBuilder: Failed to parse designJson string', err);
      return null;
    }
  }
  return designJson;
};

const extractBlocksFromDesignJson = (designJson: any, depth = 0): EmailBlockData[] | null => {
  if (!designJson || typeof designJson !== 'object' || depth > MAX_DESIGN_PARSE_DEPTH) {
    return null;
  }
  if (Array.isArray((designJson as any).blocks)) {
    return (designJson as any).blocks as EmailBlockData[];
  }
  if ((designJson as any).designJson) {
    return extractBlocksFromDesignJson((designJson as any).designJson, depth + 1);
  }
  if ((designJson as any).state) {
    return extractBlocksFromDesignJson((designJson as any).state, depth + 1);
  }
  return null;
};

const looksLikeLegacyDesignJson = (designJson: any): boolean => {
  if (!designJson || typeof designJson !== 'object') {
    return false;
  }
  if (Array.isArray((designJson as any).blocks)) {
    return false;
  }
  const legacyIndicators = [
    Boolean((designJson as any).body),
    Array.isArray((designJson as any).rows),
    Array.isArray((designJson as any).content?.rows),
    typeof (designJson as any).schemaVersion === 'number',
  ];
  return legacyIndicators.some(Boolean);
};

const hasDefaultPlaceholders = (blocks: EmailBlockData[]): { hasPlaceholders: boolean; blocksWithPlaceholders: string[]; blockIdsWithPlaceholders: string[] } => {
  if (!blocks || !Array.isArray(blocks)) return { hasPlaceholders: false, blocksWithPlaceholders: [], blockIdsWithPlaceholders: [] };

  const defaultTexts = [
    'This is a text block. Click to edit.',
    'Heading',
    'Display Title',
    'Supporting subtitle text',
    '"A beautiful quote to inspire your readers and add visual interest to your email."',
    'Caption text for images or descriptions',
    'Preview text that appears in email inbox before opening',
    'Welcome to Our Newsletter',
    'This is a great place to share your message with your audience. Customize this text to match your brand and communication style.'
  ];

  const defaultImageIndicators = [
    '/images/Email Builder/DEFAULT LOGO.png',
    'via.placeholder.com',
    'api/placeholder',
    'images.unsplash.com',
    'example.com'
  ];

  const foundBlocks = new Set<string>();
  const foundBlockIds = new Set<string>();

  const checkBlocksList = (blockList: EmailBlockData[]) => {
    for (const block of blockList) {
      if (!block || !block.content) continue;

      let isPlaceholder = false;

      // Check text/headings
      const contentValues = [
        block.content.text,
        block.content.title,
        block.content.subtitle,
        block.content.description,
        block.content.prompt,
        block.content.question,
        block.content.sectionTitle,
        block.content.author
      ];
      for (const val of contentValues) {
        if (typeof val === 'string' && defaultTexts.includes(val.trim())) {
          isPlaceholder = true;
        }
      }

      // Check images
      if (block.type === 'image' || block.type === 'logo' || block.type === 'giphy') {
         if (!block.content.src) isPlaceholder = true;
      }
      const imageValues = [
        block.content.src,
        block.content.image,
        block.content.heroImage,
        block.content.coverImage,
        block.content.thumbnail,
        block.content.thumbnailUrl
      ];
      for (const val of imageValues) {
        if (typeof val === 'string' && defaultImageIndicators.some(indicator => val.includes(indicator))) {
          isPlaceholder = true;
        }
      }

      // Check URLs
      const urlValues = [
        block.content.url,
        block.content.buttonUrl,
        block.content.actionUrl,
        block.content.primaryButtonUrl
      ];
      for (const val of urlValues) {
        if (typeof val === 'string' && (val.trim() === '#' || val.includes('example.com'))) {
          isPlaceholder = true;
        }
      }

      // Videos
      if (block.type === 'video') {
        const vUrl = block.content.videoUrl;
        if (!vUrl || vUrl.includes('example.com') || vUrl.includes('dQw4w9WgXcQ')) isPlaceholder = true;
      }

      // Socials
      if (block.type === 'social' && Array.isArray(block.content.platforms)) {
        for (const platform of block.content.platforms) {
          if (!platform.url || platform.url.trim() === '#' || platform.url.includes('example.com')) isPlaceholder = true;
        }
      }

      // Products
      if (block.type === 'product' || block.type === 'product-rec') {
        const productsToCheck = block.content.products || [block.content];
        for (const p of productsToCheck) {
          if (p.name === 'Product Name' || p.name === 'Product 1') isPlaceholder = true;
          if (p.url === '#') isPlaceholder = true;
          if (p.image && defaultImageIndicators.some(indicator => p.image.includes(indicator))) isPlaceholder = true;
        }
      }

      if (isPlaceholder) {
        const readableName = block.type === 'video' ? 'Video' : block.type
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        foundBlocks.add(readableName);
        foundBlockIds.add(block.id);
      }
      
      // Columns
      if (block.type === 'columns' && Array.isArray(block.content.columnContent)) {
        for (const col of block.content.columnContent) {
          if (Array.isArray(col)) {
            checkBlocksList(col);
          }
        }
      }
    }
  };

  checkBlocksList(blocks);
  return {
    hasPlaceholders: foundBlocks.size > 0,
    blocksWithPlaceholders: Array.from(foundBlocks),
    blockIdsWithPlaceholders: Array.from(foundBlockIds)
  };
};

// Drop Zone Between Blocks Component
// Improved: Larger clickable area and hover expansion for easier targeting
const BlockDropZone: React.FC<{
  id: string;
  index: number;
  activeDragId: string | null;
  isFromPalette: boolean;
}> = ({ id, index, activeDragId, isFromPalette }) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      index,
      accepts: ['block', 'layout'],
    },
  });

  const showIndicator = isOver && isFromPalette;

  return (
    <div
      ref={setNodeRef}
      className={`relative transition-all ${showIndicator ? 'h-10' : 'h-2 -my-1'}`}
    >
      {/* Only show indicator when actively dragging a block */}
      {showIndicator && (
        <div className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 h-0.5 bg-slate-900 z-10" />
      )}
    </div>
  );
};

// Canvas Drop Zone Component
const CanvasDropZone: React.FC<{ children: React.ReactNode; blocks: EmailBlockData[]; activeDragId: string | null }> = ({
  children,
  blocks,
  activeDragId,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas-drop-zone',
    data: {
      accepts: ['block', 'layout'],
      index: -1, // Special index for empty canvas
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`email-canvas min-h-full ${isOver && activeDragId ? 'ring-2 ring-slate-800 bg-slate-100' : ''
        }`}
      style={{ minHeight: '100%' }}
    >
      {children}
    </div>
  );
};

const ModernEmailBuilder: React.FC<ModernEmailBuilderProps> = ({
  template,
  campaignName,
  campaignId,
  workspaceId,
  onSave,
  onSaveAndNext,
  onCancel,
  onCampaignNameUpdate,
  onManualSaveComplete,
  canRemoveBranding = false,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeUserId, currentWorkspace } = useWorkspace();
  const { showSuccess, showError } = useNotification();
  // Senders belong to the account being viewed, which is the impersonated user
  // when an admin has entered a profile.
  const ownerId = activeUserId || currentUser?.uid;
  const effectiveCanRemoveBranding = canRemoveBranding || !!currentWorkspace?.settings?.branding?.removeScubamailBranding;

  // Loading screen — shows for ~3s on first mount
  const [isLoading, setIsLoading] = useState(true);

  // For poll tracking - use prop or fallback to context
  const effectiveWorkspaceId = workspaceId || currentWorkspace?.id;
  const brandingOptions = useMemo(() => {
    if (!currentWorkspace?.settings?.branding) return undefined;
    const { primaryColor, secondaryColor, socialLinks, emailSignature } = currentWorkspace.settings.branding;
    return {
      primaryColor,
      secondaryColor,
      socialLinks,
      emailSignature
    };
  }, [currentWorkspace]);

  const [templateName, setTemplateName] = useState(
    template?.name || campaignName || 'Untitled Email'
  );
  const [templateSubject, setTemplateSubject] = useState(
    template?.subject || ''
  );
  // Initialize blocks - start with empty array, useEffect will load them
  // This prevents initializing with default blocks when template is loading
  const [blocks, setBlocks] = useState<EmailBlockData[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [selectedColumnBlock, setSelectedColumnBlock] = useState<{ parentId: string; columnIndex: number; blockIndex: number } | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [status, setStatus] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<'info' | 'success' | 'error'>('info');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [editedCampaignName, setEditedCampaignName] = useState(campaignName || '');
  const [isEditingCampaignName, setIsEditingCampaignName] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewViewMode, setPreviewViewMode] = useState<'desktop' | 'tablet' | 'mobile' | 'inbox'>('desktop');
  const [previewShareUrl, setPreviewShareUrl] = useState<string | null>(null);
  const [previewShareLoading, setPreviewShareLoading] = useState(false);
  const [previewLinkCopied, setPreviewLinkCopied] = useState(false);
  const previewIdRef = useRef<string | null>(null);
  const [templateStyles, setTemplateStyles] = useState<EmailTemplateStyles>(
    template?.styles || DEFAULT_STYLES
  );
  // const [isDarkModePreview, setIsDarkModePreview] = useState(false); // Removed
  const [isDarkModePreview, setIsDarkModePreview] = useState(false);

  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [verifiedEmails, setVerifiedEmails] = useState<EmailVerification[]>([]);
  const [verifiedDomains, setVerifiedDomains] = useState<DomainVerification[]>([]);
  const [sendersLoading, setSendersLoading] = useState(false);
  const [testFromEmail, setTestFromEmail] = useState('');
  const [testFromName, setTestFromName] = useState('');
  const [actualSendingEmail, setActualSendingEmail] = useState('');
  const [isTestEmailSent, setIsTestEmailSent] = useState(false);

  const [previewEmail, setPreviewEmail] = useState({
    to: "Recipient's email address",
    from: '',
    subject: templateSubject,
    previewText: '',
  });
  const [enableMergeTags, setEnableMergeTags] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const saveMenuRef = useRef<HTMLDivElement>(null);
  const [savingMode, setSavingMode] = useState<'save' | 'next' | null>(null);
  const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false);
  const [historyStack, setHistoryStack] = useState<BuilderHistoryState[]>([]);
  const [futureStack, setFutureStack] = useState<BuilderHistoryState[]>([]);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [validationErrorBlocks, setValidationErrorBlocks] = useState<string[]>([]);
  const [validationErrorBlockIds, setValidationErrorBlockIds] = useState<string[]>([]);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [importPendingData, setImportPendingData] = useState<any>(null);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingAutoSaveSnapshotRef = useRef<string | null>(null);
  const lastSavedSnapshotRef = useRef<string>('');
  const hasInitializedAutoSaveRef = useRef(false);
  const performAutoSaveRef = useRef<() => void>(() => { });
  const historyInitializedRef = useRef(false);
  const isRestoringHistoryRef = useRef(false);
  const lastHistorySnapshotRef = useRef<string>('');
  const lastHistoryStateRef = useRef<BuilderHistoryState | null>(null);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [addPanelCategory, setAddPanelCategory] = useState<'basic' | 'layouts' | 'sections' | 'saved' | 'branding' | 'structure' | null>(null);
  const [isBlockPaletteCollapsed, setIsBlockPaletteCollapsed] = useState(true);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [canvasZoom, setCanvasZoom] = useState(100);
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const [designPopover, setDesignPopover] = useState<'colors' | 'font' | 'footer' | 'template' | null>(null);
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const fontDropdownRef = useRef<HTMLDivElement>(null);
  const designPopoverRef = useRef<HTMLDivElement>(null);
  const toolbarImportRef = useRef<HTMLInputElement>(null);
  // Stores the computed position for the current open popover
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

  const openPopover = useCallback((key: typeof designPopover, btn: HTMLElement | null) => {
    if (!btn || key === designPopover) {
      setDesignPopover(null);
      setPopoverPos(null);
      return;
    }
    const rect = btn.getBoundingClientRect();
    // Center the popover horizontally under the button
    setPopoverPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    setDesignPopover(key);
  }, [designPopover]);  const formattedAddress = useMemo(() => {
    const address = currentWorkspace?.settings?.address;
    if (!address) return '';
    const line1 = [address.addressLine1, address.addressLine2].filter(l => l?.trim()).join(', ');
    const line2 = [
      address.city ? `${address.city}${address.region ? `, ${address.region}` : ''} ${address.postalCode || ''}` : '',
      address.country
    ].filter(l => l?.trim()).join(', ');
    const result = [line1, line2].filter(l => l);
    return result.length > 0 ? result : undefined;
  }, [currentWorkspace?.settings?.address]);

  // Sync preview email subject with template subject
  useEffect(() => {
    setPreviewEmail(prev => ({ ...prev, subject: templateSubject }));
  }, [templateSubject]);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(event.target as Node)) {
        setSaveMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Global Font Loader: Ensures all fonts used in blocks or global styles are loaded in the editor
  useEffect(() => {
    const fontsToLoad = new Set<string>();
    if (templateStyles.fontFamily) fontsToLoad.add(templateStyles.fontFamily);
    
    blocks.forEach(block => {
      if (block.styles?.fontFamily) fontsToLoad.add(block.styles.fontFamily);
      if (block.styles?.productNameFontFamily) fontsToLoad.add(block.styles.productNameFontFamily);
      if (block.styles?.headingFontFamily) fontsToLoad.add(block.styles.headingFontFamily);
    });

    const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Century Gothic', 'Times New Roman', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Impact', 'Comic Sans MS', 'Courier New', 'Lucida Console', 'Monaco', 'Consolas', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI'];
    
    const googleFonts = Array.from(fontsToLoad).filter(f => f && !systemFonts.includes(f));
    
    if (googleFonts.length > 0) {
      const linkId = 'google-fonts-editor-link';
      let linkElement = document.getElementById(linkId) as HTMLLinkElement;
      
      const familyString = googleFonts.map(f => `family=${encodeURIComponent(f)}:wght@400;500;600;700;800;900`).join('&');
      const href = `https://fonts.googleapis.com/css2?${familyString}&display=swap`;
      
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.id = linkId;
        linkElement.rel = 'stylesheet';
        document.head.appendChild(linkElement);
      }
      
      if (linkElement.href !== href) {
        linkElement.href = href;
      }
    }
  }, [blocks, templateStyles.fontFamily]);

  // Handle keyboard shortcuts (Escape, Delete/Backspace, Ctrl+A, Undo/Redo)
  const handleKeyboardShortcut = useCallback(
    (event: KeyboardEvent) => {
      // Check if user is typing in an input field
      const target = event.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      if (event.key === 'Escape') {
        setSaveMenuOpen(false);
        setSelectedBlockId(null);
        setIsShortcutsModalOpen(false);
      }

      // Delete selected block with Delete or Backspace key (only if not typing)
      if ((event.key === 'Delete' || event.key === 'Backspace') && !isTyping && selectedBlockId) {
        // Check if the selected block is a protected branding block
        const selectedBlock = blocks.find((b) => b.id === selectedBlockId);
        if (selectedBlock?.isScubamailBranding && !effectiveCanRemoveBranding) {
          event.preventDefault();
          return; // Block deletion - user needs to upgrade
        }
        event.preventDefault();
        setBlocks((prev) => prev.filter((block) => block.id !== selectedBlockId));
        setSelectedBlockId(null);
        if (selectedColumnBlock?.parentId === selectedBlockId) {
          setSelectedColumnBlock(null);
        }
      }

      // Ctrl+A / Cmd+A - Select first block or show clear all option
      if ((event.ctrlKey || event.metaKey) && event.key === 'a' && !isTyping) {
        event.preventDefault();
        if (blocks.length > 0) {
          if (selectedBlockId) {
            // If already have selection and user presses Ctrl+A again, ask to clear all
            const confirmClear = window.confirm(`Delete all ${blocks.length} blocks from the canvas?`);
            if (confirmClear) {
              setBlocks([]);
              setSelectedBlockId(null);
              setSelectedColumnBlock(null);
            }
          } else {
            // Select the first block
            setSelectedBlockId(blocks[0].id);
          }
        }
      }

      // Cmd+Z / Ctrl+Z - Undo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey && !isTyping) {
        event.preventDefault();
        if (historyStack.length > 0) {
          const previousState = historyStack[historyStack.length - 1];
          setFutureStack((prev) => [{ blocks: [...blocks], templateName, templateSubject, styles: templateStyles }, ...prev]);
          isRestoringHistoryRef.current = true;
          setBlocks(previousState.blocks);
          setTemplateName(previousState.templateName);
          setTemplateSubject(previousState.templateSubject);
          setTemplateStyles(previousState.styles);
          setHistoryStack((prev) => prev.slice(0, -1));
        }
      }

      // Cmd+Shift+Z / Ctrl+Shift+Z - Redo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey && !isTyping) {
        event.preventDefault();
        if (futureStack.length > 0) {
          const [nextState, ...rest] = futureStack;
          setHistoryStack((prev) => [...prev, { blocks: [...blocks], templateName, templateSubject, styles: templateStyles }]);
          isRestoringHistoryRef.current = true;
          setBlocks(nextState.blocks);
          setTemplateName(nextState.templateName);
          setTemplateSubject(nextState.templateSubject);
          setTemplateStyles(nextState.styles);
          setFutureStack(rest);
        }
      }
    },
    [selectedBlockId, selectedColumnBlock, blocks, historyStack, futureStack, templateName, templateSubject, effectiveCanRemoveBranding]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, [handleKeyboardShortcut]);

  useEffect(() => {
    setHistoryStack([]);
    setFutureStack([]);
    historyInitializedRef.current = false;
    isRestoringHistoryRef.current = false;
    lastHistorySnapshotRef.current = '';
    lastHistoryStateRef.current = null;
  }, [template?.id, campaignId]);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const computeTemplateSnapshot = useCallback(
    () =>
      JSON.stringify({
        name: (templateName || '').trim(),
        subject: (templateSubject || '').trim(),
        blocks,
        styles: templateStyles,
      }),
    [templateName, templateSubject, blocks, templateStyles]
  );

  const enqueueAutoSave = useCallback(
    (snapshot: string, delay = AUTOSAVE_DEBOUNCE_MS) => {
      if (!snapshot) return;
      setAutoSaveStatus('pending');
      setAutoSaveError(null);
      pendingAutoSaveSnapshotRef.current = snapshot;
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        performAutoSaveRef.current?.();
      }, delay);
    },
    []
  );

  useEffect(() => {
    const snapshot = computeTemplateSnapshot();
    const currentState: BuilderHistoryState = {
      blocks: cloneBlocks(blocks),
      templateName,
      templateSubject,
      styles: templateStyles,
    };

    if (!historyInitializedRef.current) {
      historyInitializedRef.current = true;
      lastHistorySnapshotRef.current = snapshot;
      lastHistoryStateRef.current = currentState;
      return;
    }

    if (isRestoringHistoryRef.current) {
      isRestoringHistoryRef.current = false;
      lastHistorySnapshotRef.current = snapshot;
      lastHistoryStateRef.current = currentState;
      return;
    }

    if (snapshot === lastHistorySnapshotRef.current) {
      return;
    }

    setHistoryStack((prev) => {
      const next = lastHistoryStateRef.current ? [...prev, lastHistoryStateRef.current] : prev;
      return next.slice(-MAX_HISTORY_ENTRIES);
    });
    setFutureStack([]);
    lastHistorySnapshotRef.current = snapshot;
    lastHistoryStateRef.current = currentState;
  }, [blocks, templateName, templateSubject, computeTemplateSnapshot]);

  useEffect(() => {
    const snapshot = computeTemplateSnapshot();
    if (!hasInitializedAutoSaveRef.current) {
      hasInitializedAutoSaveRef.current = true;
      lastSavedSnapshotRef.current = snapshot;
      return;
    }
    if (snapshot === lastSavedSnapshotRef.current) return;
    enqueueAutoSave(snapshot);
  }, [computeTemplateSnapshot, enqueueAutoSave]);

  // Create default starter template with Header, Body, Footer sections
  const createDefaultStarterTemplate = useCallback((): EmailBlockData[] => {
    const id = () => generateId();
    return [
      // Logo / header
      {
        id: id(), type: 'logo',
        content: { src: '', alt: 'Your Logo', width: '140px', align: 'center', linkUrl: '' },
        styles: { padding: '24px 20px 16px 20px', backgroundColor: '#ffffff', textAlign: 'center' },
      },
      // Divider under header
      {
        id: id(), type: 'divider',
        content: {},
        styles: { padding: '0px 20px', borderColor: '#E5E7EB', borderWidth: '1px', borderStyle: 'solid' },
      },
      // Hero heading
      {
        id: id(), type: 'heading',
        content: { text: 'Your headline goes here', level: 1 },
        styles: { padding: '40px 32px 12px 32px', fontSize: '32px', fontWeight: '700', color: '#111827', textAlign: 'center', lineHeight: '120%' },
      },
      // Body text
      {
        id: id(), type: 'text',
        content: { text: 'This is where your main message goes. Describe your offer, update, or announcement clearly. Keep it concise — 2-3 sentences is ideal.' },
        styles: { padding: '0px 32px 28px 32px', fontSize: '16px', color: '#374151', textAlign: 'center', lineHeight: '160%' },
      },
      // CTA button
      {
        id: id(), type: 'button',
        content: { text: 'Get Started', url: 'https://example.com', actionType: 'open-url' },
        styles: {
          padding: '0px 32px 40px 32px', textAlign: 'center',
          buttonStyle: { backgroundColor: '#111827', color: '#FFFFFF', padding: '14px 32px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', width: 'auto' },
        },
      },
      // Divider before footer
      {
        id: id(), type: 'divider',
        content: {},
        styles: { padding: '0px 20px', borderColor: '#E5E7EB', borderWidth: '1px', borderStyle: 'solid' },
      },
      // Footer
      {
        id: id(), type: 'footer',
        content: {
          template: 'standard',
          text: '© 2025 Your Company. All rights reserved.',
          links: [
            { text: 'Unsubscribe', url: '{{unsubscribe_url}}' },
            { text: 'Privacy Policy', url: '#' },
          ],
        },
        styles: { padding: '24px 20px', backgroundColor: '#F9FAFB', textAlign: 'center', fontSize: '12px', color: '#6B7280' },
      },
    ];
  }, []);

  const ensureBranding = useCallback((blocksList: EmailBlockData[]): EmailBlockData[] => {
    // Branding is now handled as a mandatory footer in the email generator
    return blocksList.filter(b => !b.isScubamailBranding);
  }, []);

  // Load verified senders
  const loadVerifiedSenders = useCallback(async () => {
    if (!currentUser || !effectiveWorkspaceId) return;

    try {
      setSendersLoading(true);
      const [emails, domains] = await Promise.all([
        emailVerificationService.getUserVerifiedEmails(ownerId!, effectiveWorkspaceId),
        domainVerificationService.getUserDomainVerifications(ownerId!)
      ]);

      const workspaceDomains = domains.filter(domain => domain.status === 'verified');
      setVerifiedEmails(emails);
      setVerifiedDomains(workspaceDomains);

      // Auto-select first available sender if not already set
      if (!testFromEmail) {
        if (emails.length > 0) {
          setTestFromEmail(emails[0].email);
        } else if (workspaceDomains.length > 0) {
          setTestFromEmail(`info@${workspaceDomains[0].domain}`);
        }
      }
      
      if (!testFromName) {
        setTestFromName(currentWorkspace?.name || 'Scubamail');
      }
    } catch (error) {
      console.error('Error loading verified senders:', error);
    } finally {
      setSendersLoading(false);
    }
  }, [currentUser, ownerId, effectiveWorkspaceId, testFromEmail, testFromName, currentWorkspace?.name]);

  useEffect(() => {
    if (showTestEmailModal) {
      loadVerifiedSenders();
    }
  }, [showTestEmailModal, loadVerifiedSenders]);

  useEffect(() => {
    const updateActualSendingEmail = async () => {
      if (testFromEmail && currentUser && effectiveWorkspaceId) {
        try {
          const actual = await sendingDomainService.getSendingAddressForEmail(
            ownerId!,
            testFromEmail,
            effectiveWorkspaceId
          );
          setActualSendingEmail(actual);
        } catch (error) {
          console.error('Error fetching actual sending address:', error);
          setActualSendingEmail('');
        }
      } else {
        setActualSendingEmail('');
      }
    };
    updateActualSendingEmail();
  }, [testFromEmail, currentUser, effectiveWorkspaceId]);

  const createHtmlBlocksFromHtml = (html?: string): EmailBlockData[] => {
    if (!html || !html.trim()) {
      return [];
    }
    return [
      {
        id: generateId(),
        type: 'code',
        section: 'body',
        content: { code: html },
        styles: {
          padding: '0px',
        },
      },
    ];
  };

  // Track if blocks have been initialized to prevent re-initialization during auto-saves
  const blocksInitializedRef = useRef(false);
  const lastTemplateIdRef = useRef<string | undefined>(template?.id);

  // Load template blocks on mount or when template ID changes (not on designJson changes)
  useEffect(() => {
    // Only initialize blocks once or when template ID actually changes
    const currentTemplateId = template?.id;
    const hasTemplateIdChanged = currentTemplateId !== lastTemplateIdRef.current;

    // Check if template was null/undefined and now has a value - need to load blocks
    const hadTemplate = lastTemplateIdRef.current !== undefined;
    const hasTemplate = currentTemplateId !== undefined;
    const templateJustLoaded = !hadTemplate && hasTemplate;

    // Reset initialization if template ID changed or template just loaded
    if (hasTemplateIdChanged || templateJustLoaded) {
      if (hasTemplateIdChanged) {
        console.log('Template ID changed, resetting blocks initialization');
      }
      if (templateJustLoaded) {
        console.log('Template just became available, resetting initialization flag');
      }
      blocksInitializedRef.current = false;
      lastTemplateIdRef.current = currentTemplateId;
    }

    // Skip re-initialization if blocks are already loaded and template hasn't changed
    if (blocksInitializedRef.current && !hasTemplateIdChanged && !templateJustLoaded) {
      return;
    }

    // Check if we have a valid template with blocks
    if (template?.designJson) {
      try {
        const normalizedDesign = normalizeDesignJson(template.designJson);
        const extractedBlocks = extractBlocksFromDesignJson(normalizedDesign);
        console.log('ModernEmailBuilder loading blocks:', {
          hasDesignJson: !!template.designJson,
          designJsonType: typeof template.designJson,
          designJsonKeys: normalizedDesign ? Object.keys(normalizedDesign) : [],
          hasBlocks: !!extractedBlocks,
          blocksCount: extractedBlocks?.length || 0,
          legacyStructureDetected: looksLikeLegacyDesignJson(normalizedDesign),
        });
        if (extractedBlocks && extractedBlocks.length > 0) {
          const clonedBlocks = cloneBlocks(extractedBlocks);
          console.log('Loading blocks into editor:', clonedBlocks.length, 'blocks');
          setBlocks(ensureBranding(clonedBlocks));
          blocksInitializedRef.current = true;
          console.log('Blocks loaded successfully, blocksInitializedRef set to true');
          return;
        }

        // Check if designJson has an empty blocks array - if so, don't fallback to HTML
        // This prevents forcing templates into code view when they should be editable
        const hasEmptyBlocksArray = normalizedDesign &&
          typeof normalizedDesign === 'object' &&
          Array.isArray(normalizedDesign.blocks) &&
          normalizedDesign.blocks.length === 0;

        if (hasEmptyBlocksArray) {
          console.log('ModernEmailBuilder: Template has empty blocks array, keeping empty (will allow building from scratch)');
          setBlocks(ensureBranding([]));
          blocksInitializedRef.current = true;
          return;
        }

        if (template.htmlContent) {
          const htmlFallbackBlocks = createHtmlBlocksFromHtml(template.htmlContent);
          if (htmlFallbackBlocks.length > 0) {
            console.warn('ModernEmailBuilder: designJson missing blocks, using HTML fallback block', {
              legacyStructureDetected: looksLikeLegacyDesignJson(normalizedDesign),
            });
            setBlocks(ensureBranding(htmlFallbackBlocks));
            blocksInitializedRef.current = true;
            return;
          }
        }

        console.warn('Template has designJson but no usable blocks array.', {
          legacyStructureDetected: looksLikeLegacyDesignJson(normalizedDesign),
        });
      } catch (err) {
        console.error('Failed to load template blocks:', err);
        if (template?.htmlContent) {
          const htmlFallbackBlocks = createHtmlBlocksFromHtml(template.htmlContent);
          if (htmlFallbackBlocks.length > 0) {
            console.warn('ModernEmailBuilder: Falling back to HTML after error loading designJson');
            setBlocks(ensureBranding(htmlFallbackBlocks));
            blocksInitializedRef.current = true;
            return;
          }
        }
      }
    } else if (template?.htmlContent) {
      const htmlFallbackBlocks = createHtmlBlocksFromHtml(template.htmlContent);
      if (htmlFallbackBlocks.length > 0) {
        console.log('ModernEmailBuilder: Template missing designJson but has HTML, using fallback block');
        setBlocks(ensureBranding(htmlFallbackBlocks));
        blocksInitializedRef.current = true;
        return;
      }
    } else {
      console.log('ModernEmailBuilder: No template or designJson provided');
    }

    // If no template exists or template has no blocks, create default starter template
    // Only do this if blocks haven't been initialized yet AND there's no template (i.e., building from scratch)
    if (!blocksInitializedRef.current) {
      // Only create default blocks if there's no template at all (building from scratch)
      // If template exists but has no blocks, keep empty array
      if (!template) {
        console.log('No template provided, creating default starter template');
        const defaultBlocks = createDefaultStarterTemplate();
        setBlocks(ensureBranding(defaultBlocks));
        blocksInitializedRef.current = true;
      } else if (!template.designJson) {
        // Template exists but no designJson - this shouldn't happen normally, but handle gracefully
        console.log('Template exists but no designJson, creating default starter template');
        const defaultBlocks = createDefaultStarterTemplate();
        setBlocks(ensureBranding(defaultBlocks));
        blocksInitializedRef.current = true;
      } else {
        // Template exists with designJson but no usable blocks or HTML fallback
        console.log('Template has designJson but no blocks and no HTML fallback, keeping empty blocks array');
        blocksInitializedRef.current = true;
      }
    } else {
      console.log('Blocks already initialized, skipping default template creation');
    }
  }, [template?.id, template?.designJson, template?.htmlContent, createDefaultStarterTemplate]); // Watch both template ID and designJson to reload blocks when template changes

  useEffect(() => {
    setTemplateName(template?.name || campaignName || 'Untitled Email');
    if (template?.styles) {
      setTemplateStyles(template.styles);
    } else {
      setTemplateStyles(DEFAULT_STYLES);
    }
  }, [template?.id, template?.name, campaignName]);

  useEffect(() => {
    setTemplateSubject(template?.subject || '');
  }, [template?.subject]);

  useEffect(() => {
    setEditedCampaignName(campaignName || '');
  }, [campaignName]);

  useEffect(() => {
    if (selectedColumnBlock && !blocks.some((block) => block.id === selectedColumnBlock.parentId)) {
      setSelectedColumnBlock(null);
    }
  }, [blocks, selectedColumnBlock]);

  const getBlockFromDragData = useCallback(
    (dragData: any, dragId: string, options?: { assignSection?: boolean }) => {
      let block: EmailBlockData;

      // Prevent creating blocks with type "layout" - layouts should never be created as blocks
      // They should be expanded into their component blocks instead
      if (dragData?.type === 'layout' && dragData?.layout) {
        throw new Error('Cannot create block from layout - layouts must be expanded into component blocks');
      }

      if (dragData?.savedBlock) {
        block = {
          ...JSON.parse(JSON.stringify(dragData.savedBlock)),
          id: generateId(),
        };
      } else if (dragData?.contentFile) {
        block = createBlockFromContentFile(dragData.contentFile);
      } else {
        const type = dragData?.type || dragId.split('-')[1] || dragId;
        // Prevent creating blocks with type "layout"
        if (type === 'layout') {
          throw new Error('Cannot create block with type "layout" - layouts must be expanded');
        }
        block = createBlockFromType(type);

        // Apply buttonStyle if present (from styled button previews)
        if (type === 'button' && dragData?.buttonStyle) {
          const styleData = dragData.buttonStyle;
          const btnStyle = styleData.buttonStyle || {};

          block.content.text = styleData.buttonText || block.content.text;

          // Determine if this is a gradient button
          const hasGradient = btnStyle.background && btnStyle.background.includes('linear-gradient');
          // Determine if this is an outline button
          const isOutline = btnStyle.border && btnStyle.border !== 'none' && (!btnStyle.backgroundColor || btnStyle.backgroundColor === 'transparent');

          block.styles.buttonStyle = {
            ...block.styles.buttonStyle,
            // Set variant based on style
            variant: hasGradient ? 'gradient' : (isOutline ? 'outline' : 'solid'),
            // For gradients, use the gradient property
            gradient: hasGradient ? btnStyle.background : undefined,
            // Background color
            backgroundColor: btnStyle.backgroundColor || (hasGradient ? '#667EEA' : '#3B82F6'),
            // Text color
            color: btnStyle.color || '#FFFFFF',
            // For outline buttons, set border color
            borderColor: isOutline ? (btnStyle.color || '#3B82F6') : undefined,
            // Padding - use larger padding for canvas than preview
            padding: '12px 24px',
            borderRadius: btnStyle.borderRadius || '6px',
            fontSize: '16px',
            fontWeight: btnStyle.fontWeight || '600',
            letterSpacing: btnStyle.letterSpacing,
            // Shadow - map boxShadow to shadow property
            shadow: btnStyle.boxShadow ? (btnStyle.boxShadow.includes('4px') ? 'hard' : 'soft') : undefined,
          };
        }
      }

      if (options?.assignSection) {
        block.section = 'body';
        if (!dragData?.savedBlock && !dragData?.contentFile && block.styles) {
          block.styles.textAlign = block.styles.textAlign || 'center';
        }
      }

      return block;
    },
    []
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    // Close the floating add panel after a drop from the palette
    if (active.data.current?.fromPalette) {
      setIsAddPanelOpen(false);
      setAddPanelCategory(null);
    }

    // Handle dropping from palette to canvas
    if (active.data.current?.fromPalette) {
      // Check if it's a layout template - layouts should NEVER be inserted into columns
      const isLayout = active.data.current?.type === 'layout' && active.data.current?.layout;

      // If it's NOT a layout, check if it should be inserted into a column
      if (!isLayout && over) {
        const overId = over.id.toString();

        // Handle dropping between blocks in a column
        if (overId.startsWith('column-block-drop-')) {
          const columnData = over.data?.current;
          if (columnData?.type === 'column-block' && columnData?.blockId && typeof columnData?.columnIndex === 'number' && typeof columnData?.insertIndex === 'number') {
            // Use functional update to avoid dependency on blocks
            setBlocks((prev) => {
              // Find the column block from previous state
              const columnBlock = prev.find(b => b.id === columnData.blockId);
              if (columnBlock && columnBlock.type === 'columns') {
                const newColumnBlock = getBlockFromDragData(active.data.current, active.id as string);

                // Prevent dropping a footer block into a column that already contains a footer
                if (newColumnBlock.type === 'footer') {
                  const inferredColumns =
                    columnBlock.content.columns ||
                    columnBlock.content.columnWidths?.length ||
                    (Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent.length : 0) ||
                    2;

                  const updatedColumns = Array.from(
                    { length: inferredColumns },
                    (_, idx) => {
                      const existing = Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent[idx] : '';
                      // Normalize to array format
                      if (!existing || existing === '') {
                        return [];
                      } else if (Array.isArray(existing)) {
                        return [...existing];
                      } else if (typeof existing === 'object' && existing.type) {
                        return [existing];
                      }
                      return [];
                    }
                  );

                  // Check if the target column already has a footer block
                  const targetColumn = updatedColumns[columnData.columnIndex] || [];
                  const hasFooterInColumn = targetColumn.some((block: any) => block.type === 'footer');

                  if (hasFooterInColumn) {
                    // Don't allow dropping footer into a column that already has a footer
                    return prev;
                  }
                }

                const inferredColumns =
                  columnBlock.content.columns ||
                  columnBlock.content.columnWidths?.length ||
                  (Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent.length : 0) ||
                  2;

                const updatedColumns = Array.from(
                  { length: inferredColumns },
                  (_, idx) => {
                    const existing = Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent[idx] : '';
                    // Normalize to array format
                    if (!existing || existing === '') {
                      return [];
                    } else if (Array.isArray(existing)) {
                      return [...existing];
                    } else if (typeof existing === 'object' && existing.type) {
                      return [existing];
                    }
                    return [];
                  }
                );

                // Insert the new block at the specified index
                const columnBlocks = [...updatedColumns[columnData.columnIndex]];
                columnBlocks.splice(columnData.insertIndex, 0, {
                  type: newColumnBlock.type,
                  content: newColumnBlock.content,
                  styles: newColumnBlock.styles,
                });
                updatedColumns[columnData.columnIndex] = columnBlocks;

                // Update selection after state update
                // Calculate the block index - if inserting, use insertIndex, otherwise append to end
                const finalBlockIndex = columnData.insertIndex >= 0
                  ? columnData.insertIndex
                  : columnBlocks.length - 1;
                setTimeout(() => {
                  setSelectedBlockId(columnData.blockId);
                  setSelectedColumnBlock({ parentId: columnData.blockId, columnIndex: columnData.columnIndex, blockIndex: finalBlockIndex });
                }, 0);

                return prev.map((b) =>
                  b.id === columnData.blockId
                    ? {
                      ...b,
                      content: {
                        ...b.content,
                        columnContent: updatedColumns,
                      },
                    }
                    : b
                );
              }
              return prev;
            });
            return;
          }
        }

        // Handle dropping into empty column (append to end)
        if (overId.startsWith('column-drop-')) {
          const columnData = over.data?.current;
          if (columnData?.type === 'column' && columnData?.blockId && typeof columnData?.columnIndex === 'number') {
            // Use functional update to avoid dependency on blocks
            setBlocks((prev) => {
              // Find the column block from previous state
              const columnBlock = prev.find(b => b.id === columnData.blockId);
              if (columnBlock && columnBlock.type === 'columns') {
                const newColumnBlock = getBlockFromDragData(active.data.current, active.id as string);

                // Prevent dropping a footer block into a column that already contains a footer
                if (newColumnBlock.type === 'footer') {
                  const inferredColumns =
                    columnBlock.content.columns ||
                    columnBlock.content.columnWidths?.length ||
                    (Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent.length : 0) ||
                    2;

                  const updatedColumns = Array.from(
                    { length: inferredColumns },
                    (_, idx) => {
                      const existing = Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent[idx] : '';
                      // Normalize to array format
                      if (!existing || existing === '') {
                        return [];
                      } else if (Array.isArray(existing)) {
                        return [...existing];
                      } else if (typeof existing === 'object' && existing.type) {
                        return [existing];
                      }
                      return [];
                    }
                  );

                  // Check if the target column already has a footer block
                  const targetColumn = updatedColumns[columnData.columnIndex] || [];
                  const hasFooterInColumn = targetColumn.some((block: any) => block.type === 'footer');

                  if (hasFooterInColumn) {
                    // Don't allow dropping footer into a column that already has a footer
                    return prev;
                  }
                }

                const inferredColumns =
                  columnBlock.content.columns ||
                  columnBlock.content.columnWidths?.length ||
                  (Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent.length : 0) ||
                  2;

                const updatedColumns = Array.from(
                  { length: inferredColumns },
                  (_, idx) => {
                    const existing = Array.isArray(columnBlock.content.columnContent) ? columnBlock.content.columnContent[idx] : '';
                    // Normalize to array format
                    if (!existing || existing === '') {
                      return [];
                    } else if (Array.isArray(existing)) {
                      return [...existing];
                    } else if (typeof existing === 'object' && existing.type) {
                      return [existing];
                    }
                    return [];
                  }
                );

                // Append the new block to the end
                const columnBlocks = [...updatedColumns[columnData.columnIndex]];
                columnBlocks.push({
                  type: newColumnBlock.type,
                  content: newColumnBlock.content,
                  styles: newColumnBlock.styles,
                });
                updatedColumns[columnData.columnIndex] = columnBlocks;

                // Update selection after state update - block is appended to end
                const finalBlockIndex = columnBlocks.length - 1;
                setTimeout(() => {
                  setSelectedBlockId(columnData.blockId);
                  setSelectedColumnBlock({ parentId: columnData.blockId, columnIndex: columnData.columnIndex, blockIndex: finalBlockIndex });
                }, 0);

                return prev.map((b) =>
                  b.id === columnData.blockId
                    ? {
                      ...b,
                      content: {
                        ...b.content,
                        columnContent: updatedColumns,
                      },
                    }
                    : b
                );
              }
              return prev;
            });
            return;
          }
        }
      }

      // Handle dropping from palette to canvas (including layouts)
      // This handles layouts and standalone blocks that weren't inserted into columns
      // Prebuilt blocks should ONLY be added when dropped on drop zones, not directly on existing blocks
      // Use functional update to determine insertion index
      setBlocks((prev) => {
        // Determine insertion index
        let insertIndex = prev.length; // Default to end
        let isValidDropZone = false;

        if (over) {
          // Check if dropped on a drop zone between blocks - this is valid
          if (over.id.toString().startsWith('drop-zone-')) {
            const dropIndex = over.data?.current?.index;
            if (typeof dropIndex === 'number' && dropIndex >= 0) {
              insertIndex = dropIndex;
              isValidDropZone = true;
            }
          }
          // Check if dropped on canvas drop zone (empty canvas) - this is valid
          else if (over.id === 'canvas-drop-zone') {
            insertIndex = 0; // Insert at beginning if canvas is empty, otherwise at end
            if (prev.length > 0) {
              insertIndex = prev.length;
            }
            isValidDropZone = true;
          }
          // Check if dropped on column drop zones - these are already handled above, so skip
          else if (over.id.toString().startsWith('column-')) {
            // Column drops are handled above, shouldn't reach here
            return prev;
          }
          // If dropped directly on an existing block (not a drop zone), REJECT IT
          // This prevents nesting blocks inside other blocks
          else if (prev.some(b => b.id === over.id)) {
            // Don't allow dropping blocks directly on existing blocks
            // Return previous state unchanged
            return prev;
          }
          // If dropped on something else that's not a valid drop zone, don't add
          else {
            return prev;
          }
        }

        // If not dropped on a valid drop zone, reject the drop
        if (!isValidDropZone && over) {
          return prev;
        }

        // Define prebuilt block types that should NEVER accept nested blocks
        const PREBUILT_BLOCK_TYPES = ['music-launch', 'newsletter', 'countdown', 'event-announcement', 'testimonial', 'footer'];

        // If dropped on a prebuilt block, reject it
        if (over && prev.some(b => b.id === over.id)) {
          const targetBlock = prev.find(b => b.id === over.id);
          if (targetBlock && PREBUILT_BLOCK_TYPES.includes(targetBlock.type)) {
            // Prebuilt blocks should never accept nested blocks
            return prev;
          }
        }

        // Handle layout templates
        if (active.data.current?.type === 'layout' && active.data.current?.layout) {
          const layout = active.data.current.layout as any;

          // Process all blocks from the layout
          const processedBlocks = layout.blocks.map((blockData: any) => {
            const block = {
              type: blockData.type,
              content: JSON.parse(JSON.stringify(blockData.content || {})),
              styles: JSON.parse(JSON.stringify(blockData.styles || {})),
            };

            // Normalize columnContent structure for existing column blocks
            if (block.type === 'columns' && block.content.columnContent) {
              const columnCount = block.content.columns || block.content.columnWidths?.length || block.content.columnContent.length || 2;
              const normalizedColumnContent = Array.from({ length: columnCount }, (_, idx) => {
                const columnItem = block.content.columnContent[idx];
                if (!columnItem) {
                  return [];
                }
                if (Array.isArray(columnItem)) {
                  return columnItem;
                }
                if (columnItem && typeof columnItem === 'object' && columnItem.type) {
                  return [columnItem];
                }
                return [];
              });
              block.content.columnContent = normalizedColumnContent;
            }

            return block;
          });

          // Check if layout contains footer blocks - if so, add them directly without wrapping
          const hasFooterBlocks = processedBlocks.some((block: any) => block.type === 'footer');
          if (hasFooterBlocks) {
            const newBlocks: EmailBlockData[] = [];
            processedBlocks.forEach((block: any) => {
              // Add footer blocks directly without wrapping
              if (block.type === 'footer') {
                const footerBlock: EmailBlockData = {
                  id: generateId(),
                  type: 'footer',
                  content: block.content,
                  styles: block.styles,
                  section: 'footer' as const,
                };
                newBlocks.push(footerBlock);
              } else if (block.type === 'columns') {
                // If there's a columns block in the layout, add it directly
                const columnBlock: EmailBlockData = {
                  id: generateId(),
                  type: 'columns',
                  content: block.content,
                  styles: block.styles,
                  section: 'body' as const,
                };
                newBlocks.push(columnBlock);
              } else {
                // For other non-footer blocks, add them directly (they'll be handled separately if needed)
                const regularBlock: EmailBlockData = {
                  id: generateId(),
                  type: block.type,
                  content: block.content,
                  styles: block.styles,
                  section: 'body' as const,
                };
                newBlocks.push(regularBlock);
              }
            });

            const updatedBlocks = [...prev];
            updatedBlocks.splice(insertIndex, 0, ...newBlocks);
            if (newBlocks.length > 0) {
              setTimeout(() => setSelectedBlockId(newBlocks[0].id), 0);
            }
            return updatedBlocks;
          }

          // Check if layout already has a single columns block - if so, use it directly
          if (processedBlocks.length === 1 && processedBlocks[0].type === 'columns') {
            const columnBlock: EmailBlockData = {
              id: generateId(),
              type: 'columns',
              content: processedBlocks[0].content,
              styles: processedBlocks[0].styles,
              section: 'body' as const,
            };
            const newBlocks = [...prev];
            newBlocks.splice(insertIndex, 0, columnBlock);
            setTimeout(() => setSelectedBlockId(columnBlock.id), 0);
            return newBlocks;
          }

          // Otherwise, wrap all blocks in a single column (100% width)
          const allBlocksToWrap: any[] = [];
          processedBlocks.forEach((block: any) => {
            if (block.type === 'columns' && Array.isArray(block.content.columnContent)) {
              // For column blocks, extract all blocks from all columns and flatten them
              block.content.columnContent.forEach((columnBlocks: any[]) => {
                if (Array.isArray(columnBlocks)) {
                  allBlocksToWrap.push(...columnBlocks);
                }
              });
            } else {
              // Add the block directly
              allBlocksToWrap.push(block);
            }
          });

          // Optimized: If layout has only one block, add it directly to prevent unnecessary nested arrays (helps Firestore)
          if (allBlocksToWrap.length === 1) {
            const singleBlockData = allBlocksToWrap[0];
            const newBlock: EmailBlockData = {
              id: generateId(),
              type: singleBlockData.type,
              content: singleBlockData.content,
              styles: singleBlockData.styles,
              section: 'body' as const,
            };

            const updatedBlocks = [...prev];
            updatedBlocks.splice(insertIndex, 0, newBlock);
            setTimeout(() => setSelectedBlockId(newBlock.id), 0);
            return updatedBlocks;
          }

          // Create a single columns block (100% width) containing all blocks stacked vertically
          const wrapperColumnBlock: EmailBlockData = {
            id: generateId(),
            type: 'columns',
            content: {
              columns: 1,
              columnWidths: ['100%'],
              columnContent: [allBlocksToWrap], // All blocks in a single column
            },
            styles: {
              padding: '20px',
              backgroundColor: 'transparent',
            },
            section: 'body' as const,
          };

          const newBlocks = [...prev];
          newBlocks.splice(insertIndex, 0, wrapperColumnBlock);
          setTimeout(() => setSelectedBlockId(wrapperColumnBlock.id), 0);
          return newBlocks;
        }

        // Ensure we're not trying to create a layout block
        if (active.data.current?.type === 'layout' && active.data.current?.layout) {
          // Layouts should have been handled above, if we reach here it's an error
          console.warn('Layout drop not handled properly, rejecting');
          return prev;
        }

        const newBlock = getBlockFromDragData(active.data.current, active.id as string, { assignSection: true });

        // Prevent dropping a footer block if there's already a footer block adjacent to the target position
        if (newBlock.type === 'footer') {
          const hasFooterBefore = insertIndex > 0 && prev[insertIndex - 1]?.type === 'footer';
          const hasFooterAfter = insertIndex < prev.length && prev[insertIndex]?.type === 'footer';

          if (hasFooterBefore || hasFooterAfter) {
            // Don't allow dropping footer next to another footer
            return prev;
          }
        }

        const newBlocks = [...prev];
        newBlocks.splice(insertIndex, 0, newBlock);
        // Track block usage for recent blocks feature
        recentBlocksService.trackUsage(newBlock.type);
        setTimeout(() => setSelectedBlockId(newBlock.id), 0);
        return newBlocks;
      });
      return;
    }

    // Handle reordering blocks (only if both are existing blocks)
    if (over && active.id !== over.id) {
      setBlocks((items) => {
        const hasActive = items.some(b => b.id === active.id);
        const hasOver = items.some(b => b.id === over.id);
        if (hasActive && hasOver) {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          const activeBlock = items[oldIndex];

          // Prevent moving a footer block next to another footer block
          if (activeBlock?.type === 'footer') {
            // Check if the target position or adjacent positions have footer blocks
            const hasFooterAtTarget = items[newIndex]?.type === 'footer';
            const hasFooterBefore = newIndex > 0 && items[newIndex - 1]?.type === 'footer';
            const hasFooterAfter = newIndex < items.length - 1 && items[newIndex + 1]?.type === 'footer';

            // If moving to a position next to a footer, don't allow it
            if (hasFooterAtTarget || hasFooterBefore || hasFooterAfter) {
              return items;
            }
          }

          return arrayMove(items, oldIndex, newIndex);
        }
        return items;
      });
    }
  }, []);

  const handleBlockUpdate = useCallback((blockId: string, updates: Partial<EmailBlockData>) => {
    setBlocks((prev) =>
      prev.map((block) => (block.id === blockId ? { ...block, ...updates } : block))
    );
  }, []);

  const handleBlockSelect = useCallback((blockId: string) => {
    setSelectedBlockId(blockId);
    setSelectedColumnBlock(null);
    setShowGlobalSettings(false);
  }, []);

  const handleSelectColumnBlock = useCallback((parentId: string, columnIndex: number, blockIndex: number = 0) => {
    setSelectedBlockId(parentId);
    setSelectedColumnBlock({ parentId, columnIndex, blockIndex });
    setShowGlobalSettings(false);
  }, []);

  const handleDeleteColumnBlock = useCallback((parentId: string, columnIndex: number, blockIndex: number) => {
    setBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== parentId) return block;
        const columnCount =
          block.content?.columns ||
          (Array.isArray(block.content?.columnContent) ? block.content.columnContent.length : 0) ||
          2;

        // Normalize columnContent to array format
        const columns = Array.from({ length: columnCount }, (_, idx) => {
          const existing = Array.isArray(block.content?.columnContent)
            ? block.content.columnContent[idx]
            : '';
          if (Array.isArray(existing)) {
            return [...existing];
          } else if (existing && typeof existing === 'object' && existing.type) {
            return [existing];
          } else if (typeof existing === 'string' && existing !== '') {
            return [{ type: 'text', content: { text: existing }, styles: {} }];
          }
          return [];
        });

        // Remove the specific block from the column
        const columnBlocks = columns[columnIndex] || [];
        columnBlocks.splice(blockIndex, 1);
        columns[columnIndex] = columnBlocks;

        return {
          ...block,
          content: {
            ...block.content,
            columnContent: columns,
          },
        };
      })
    );
    if (selectedColumnBlock?.parentId === parentId && selectedColumnBlock.columnIndex === columnIndex && selectedColumnBlock.blockIndex === blockIndex) {
      setSelectedColumnBlock(null);
    }
  }, [selectedColumnBlock]);

  const handleBlockDelete = useCallback(
    (blockId: string) => {
      // Check if this is a protected branding block
      const blockToDelete = blocks.find((b) => b.id === blockId);
      if (blockToDelete?.isScubamailBranding && !effectiveCanRemoveBranding) {
        return; // Block deletion - user needs to upgrade
      }
      setBlocks((prev) => prev.filter((block) => block.id !== blockId));
      if (selectedBlockId === blockId) {
        setSelectedBlockId(null);
      }
      if (selectedColumnBlock?.parentId === blockId) {
        setSelectedColumnBlock(null);
      }
    },
    [selectedBlockId, selectedColumnBlock, blocks, effectiveCanRemoveBranding]
  );

  const handleBlockDuplicate = useCallback((blockId: string) => {
    setBlocks((prev) => {
      const blockToDuplicate = prev.find((b) => b.id === blockId);
      if (!blockToDuplicate) return prev;
      const newBlock = {
        ...blockToDuplicate,
        id: generateId(),
      };
      const index = prev.findIndex((b) => b.id === blockId);
      const newBlocks = [...prev];
      newBlocks.splice(index + 1, 0, newBlock);
      return newBlocks;
    });
  }, []);

  const handleBlockMoveUp = useCallback((blockId: string) => {
    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === blockId);
      if (index <= 0) return prev; // Already at the top
      const newBlocks = [...prev];
      [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
      return newBlocks;
    });
  }, []);

  const handleBlockMoveDown = useCallback((blockId: string) => {
    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === blockId);
      if (index < 0 || index >= prev.length - 1) return prev; // Already at the bottom
      const newBlocks = [...prev];
      [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
      return newBlocks;
    });
  }, []);

  const handleInsertBlock = useCallback((blockId: string, blockType: string, position: 'above' | 'below') => {
    const newBlock = createBlockFromType(blockType);
    newBlock.section = 'body'; // Default to body section
    if (newBlock.styles) {
      newBlock.styles.textAlign = 'center';
    }

    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === blockId);
      if (index < 0) return prev;
      const insertIndex = position === 'above' ? index : index + 1;
      const newBlocks = [...prev];
      newBlocks.splice(insertIndex, 0, newBlock);
      return newBlocks;
    });

    // Select the newly inserted block
    const insertedBlockId = newBlock.id;
    setSelectedBlockId(insertedBlockId);
  }, []);

  const buildPreviewHtml = useCallback(() => {
    const html = generateEmailHTML(blocks, templateStyles, {
      enableInteractiveVideo: true,
      isMobile: previewViewMode !== 'desktop',
      previewText: previewEmail.previewText,
      pollTracking: effectiveWorkspaceId && campaignId ? {
        workspaceId: effectiveWorkspaceId,
        campaignId: campaignId
      } : undefined,
      branding: brandingOptions,
      address: formattedAddress,
      hideBranding: effectiveCanRemoveBranding
    });
    if (!isDarkModePreview) return html;
    // Inject a dark-mode wrapper so the iframe simulates dark email client rendering
    return html.replace(
      '<body',
      '<body data-dark-preview="true"'
    ).replace(
      '</head>',
      `<style>
        /* Dark mode preview simulation */
        body[data-dark-preview] { background-color: #1a1a1a !important; }
        body[data-dark-preview] .email-container { filter: invert(1) hue-rotate(180deg); }
        body[data-dark-preview] img { filter: invert(1) hue-rotate(180deg); }
      </style></head>`
    );
  }, [
    blocks,
    templateStyles,
    previewViewMode,
    previewEmail.previewText,
    effectiveWorkspaceId,
    campaignId,
    brandingOptions,
    formattedAddress,
    effectiveCanRemoveBranding,
    isDarkModePreview,
  ]);

  const ensurePreviewShareLink = useCallback(async () => {
    if (!currentUser) {
      showError('Sign in to generate a shareable preview link.');
      return;
    }

    setPreviewShareLoading(true);
    try {
      const htmlContent = buildPreviewHtml();
      const result = await emailPreviewService.createOrUpdatePreview({
        previewId: previewIdRef.current ?? undefined,
        htmlContent,
        subject: templateSubject,
        templateName: templateName,
        userId: currentUser.uid,
        workspaceId: effectiveWorkspaceId,
        campaignId,
      });
      previewIdRef.current = result.previewId;
      setPreviewShareUrl(result.url);
    } catch (error) {
      console.error('Failed to create preview link:', error);
      showError('Failed to generate preview link. Please try again.');
    } finally {
      setPreviewShareLoading(false);
    }
  }, [
    currentUser,
    buildPreviewHtml,
    templateSubject,
    templateName,
    effectiveWorkspaceId,
    campaignId,
    showError,
  ]);

  const ensurePreviewShareLinkRef = useRef(ensurePreviewShareLink);
  ensurePreviewShareLinkRef.current = ensurePreviewShareLink;

  useEffect(() => {
    if (!isPreviewModalOpen) {
      setPreviewLinkCopied(false);
      return;
    }
    ensurePreviewShareLinkRef.current();
  }, [isPreviewModalOpen]);

  const handleCopyPreviewLink = useCallback(async () => {
    if (!previewShareUrl) return;
    try {
      await navigator.clipboard.writeText(previewShareUrl);
      setPreviewLinkCopied(true);
      showSuccess('Preview link copied to clipboard');
      setTimeout(() => setPreviewLinkCopied(false), 2000);
    } catch {
      showError('Could not copy link. Please copy it manually.');
    }
  }, [previewShareUrl, showSuccess, showError]);

  const handleOpenPreviewInBrowser = useCallback(() => {
    if (!previewShareUrl) return;
    window.open(previewShareUrl, '_blank', 'noopener,noreferrer');
  }, [previewShareUrl]);

  const handleAIGenerate = useCallback((blocks: EmailBlockData[], subject: string, previewText: string) => {
    // Replace existing blocks with AI-generated ones
    setBlocks(blocks);
    // Update subject
    setTemplateSubject(subject);
    // Update preview text in preview email
    setPreviewEmail(prev => ({ ...prev, previewText }));
    // Clear selection
    setSelectedBlockId(null);
    setSelectedColumnBlock(null);
    // Show success message
    setStatus('Email generated successfully! You can now edit it.');
    setStatusTone('success');
    setTimeout(() => setStatus(null), 3000);
  }, []);

  const selectedBlock = useMemo(
    () => blocks.find((block) => block.id === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );

  const panelBlock = useMemo(() => {
    if (selectedColumnBlock) {
      const parent = blocks.find((block) => block.id === selectedColumnBlock.parentId);
      if (!parent) return null;
      const columnContentArray = Array.isArray(parent.content?.columnContent)
        ? parent.content.columnContent[selectedColumnBlock.columnIndex]
        : null;
      if (!columnContentArray) return null;

      // Handle array of blocks (new format)
      if (Array.isArray(columnContentArray)) {
        const blockItem = columnContentArray[selectedColumnBlock.blockIndex];
        if (!blockItem) return null;

        return {
          id: `${parent.id}-column-${selectedColumnBlock.columnIndex}-block-${selectedColumnBlock.blockIndex}`,
          type: blockItem.type || 'text',
          content: blockItem.content || {},
          styles: blockItem.styles || {},
          isScubamailBranding: false,
        };
      }

      // Handle legacy string format
      if (typeof columnContentArray === 'string') {
        return {
          id: `${parent.id}-column-${selectedColumnBlock.columnIndex}`,
          type: 'text',
          content: { text: columnContentArray },
          styles: {},
          isScubamailBranding: false,
        };
      }

      // Handle legacy single block object format
      return {
        id: `${parent.id}-column-${selectedColumnBlock.columnIndex}`,
        type: columnContentArray.type || 'text',
        content: columnContentArray.content || {},
        styles: columnContentArray.styles || {},
        isScubamailBranding: false,
      };
    }
    return selectedBlock;
  }, [blocks, selectedBlock, selectedColumnBlock]);

  const handleUpdateColumnBlock = useCallback(
    (parentId: string, columnIndex: number, blockIndex: number, updates: Partial<EmailBlockData>) => {
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.id !== parentId) {
            return block;
          }
          const columnCount =
            block.content?.columns ||
            (Array.isArray(block.content?.columnContent) ? block.content.columnContent.length : 0) ||
            2;

          // Normalize columnContent to array format
          const columns = Array.from({ length: columnCount }, (_, idx) => {
            const existing = Array.isArray(block.content?.columnContent)
              ? block.content.columnContent[idx]
              : '';
            if (Array.isArray(existing)) {
              return [...existing];
            } else if (existing && typeof existing === 'object' && existing.type) {
              return [existing];
            } else if (typeof existing === 'string' && existing !== '') {
              return [{ type: 'text', content: { text: existing }, styles: {} }];
            }
            return [];
          });

          // Get the column's blocks array
          const columnBlocks = columns[columnIndex] || [];
          const blockToUpdate = columnBlocks[blockIndex];

          if (!blockToUpdate) {
            // Block doesn't exist, create it
            const newBlock = createBlockFromType(updates.type || 'text');
            columnBlocks[blockIndex] = {
              ...newBlock,
              ...updates,
              content: { ...newBlock.content, ...(updates.content || {}) },
              styles: { ...newBlock.styles, ...(updates.styles || {}) },
            };
          } else {
            // Update existing block
            columnBlocks[blockIndex] = {
              ...blockToUpdate,
              ...(updates.type ? { type: updates.type } : {}),
              content: { ...(blockToUpdate.content || {}), ...(updates.content || {}) },
              styles: { ...(blockToUpdate.styles || {}), ...(updates.styles || {}) },
              ...Object.fromEntries(
                Object.entries(updates).filter(([key]) =>
                  key !== 'type' && key !== 'content' && key !== 'styles'
                )
              ),
            };
          }

          columns[columnIndex] = columnBlocks;

          return {
            ...block,
            content: {
              ...block.content,
              columnContent: columns,
            },
          };
        })
      );
    },
    []
  );

  const handleUpdateTemplateStyles = useCallback(
    (updates: Partial<EmailTemplateStyles>) => {
      setTemplateStyles((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const handlePanelBlockUpdate = useCallback(
    (updates: Partial<EmailBlockData>) => {
      if (selectedColumnBlock) {
        handleUpdateColumnBlock(
          selectedColumnBlock.parentId,
          selectedColumnBlock.columnIndex,
          selectedColumnBlock.blockIndex,
          updates
        );
      } else if (selectedBlock) {
        handleBlockUpdate(selectedBlock.id, updates);
      }
    },
    [selectedColumnBlock, selectedBlock, handleBlockUpdate, handleUpdateColumnBlock]
  );

  const buildTemplate = useCallback(
    (htmlContent: string): EmailTemplate => ({
      id: template?.id || generateId(),
      name: templateName.trim() || 'Untitled Email',
      subject: templateSubject.trim() || '',
      styles: templateStyles,
      htmlContent,
      designJson: { blocks },
      createdAt: template?.createdAt || new Date(),
      updatedAt: new Date(),
    }),
    [template, templateName, templateSubject, blocks, templateStyles]
  );

  const performAutoSave = useCallback(async () => {
    if (!pendingAutoSaveSnapshotRef.current) return;
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    try {
      setAutoSaveStatus('saving');
      setAutoSaveError(null);
      const htmlContent = generateEmailHTML(blocks, templateStyles, {
        pollTracking: effectiveWorkspaceId && campaignId ? {
          workspaceId: effectiveWorkspaceId,
          campaignId: campaignId
        } : undefined,
        branding: brandingOptions,
        address: formattedAddress,
        hideBranding: effectiveCanRemoveBranding
      });
      const payload = buildTemplate(htmlContent);
      await Promise.resolve(onSave(payload));
      const now = new Date();
      setLastSavedAt(now);
      lastSavedSnapshotRef.current = pendingAutoSaveSnapshotRef.current;
      pendingAutoSaveSnapshotRef.current = null;
      setAutoSaveStatus('saved');
    } catch (error) {
      console.error('Autosave failed:', error);
      setAutoSaveStatus('error');
      setAutoSaveError('Autosave failed. Please try again.');
    }
  }, [blocks, template, buildTemplate, onSave, effectiveWorkspaceId, campaignId, brandingOptions, templateStyles, formattedAddress, effectiveCanRemoveBranding]);

  useEffect(() => {
    performAutoSaveRef.current = performAutoSave;
  }, [performAutoSave]);

  const handleAutoSaveRetry = useCallback(() => {
    const snapshot = pendingAutoSaveSnapshotRef.current || computeTemplateSnapshot();
    if (!snapshot) return;
    enqueueAutoSave(snapshot, 500);
  }, [computeTemplateSnapshot, enqueueAutoSave]);

  const getCurrentHistoryState = useCallback((): BuilderHistoryState => {
    return {
      blocks: cloneBlocks(blocks),
      templateName,
      templateSubject,
      styles: templateStyles,
    };
  }, [blocks, templateName, templateSubject, templateStyles]);

  const handleUndo = useCallback(() => {
    setHistoryStack((prevHistory) => {
      if (prevHistory.length === 0) {
        return prevHistory;
      }
      const previousState = prevHistory[prevHistory.length - 1];
      setFutureStack((prevFuture) => [getCurrentHistoryState(), ...prevFuture]);
      isRestoringHistoryRef.current = true;
      setBlocks(previousState.blocks);
      setTemplateName(previousState.templateName);
      setTemplateSubject(previousState.templateSubject);
      setTemplateStyles(previousState.styles);
      return prevHistory.slice(0, -1);
    });
  }, [getCurrentHistoryState]);

  const handleRedo = useCallback(() => {
    setFutureStack((prevFuture) => {
      if (prevFuture.length === 0) {
        return prevFuture;
      }
      const [nextState, ...rest] = prevFuture;
      setHistoryStack((prevHistory) => {
        const nextHistory = [...prevHistory, getCurrentHistoryState()];
        return nextHistory.slice(-MAX_HISTORY_ENTRIES);
      });
      isRestoringHistoryRef.current = true;
      setBlocks(nextState.blocks);
      setTemplateName(nextState.templateName);
      setTemplateSubject(nextState.templateSubject);
      setTemplateStyles(nextState.styles);
      return rest;
    });
  }, [getCurrentHistoryState]);

  const canUndo = historyStack.length > 0;
  const canRedo = futureStack.length > 0;

  const handleSave = useCallback(
    async (mode: 'save' | 'next', ignorePlaceholders: boolean = false) => {
      if (savingMode) return;
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
      pendingAutoSaveSnapshotRef.current = null;
      setSaveMenuOpen(false);
      setStatus(null);
      try {
        const htmlContent = generateEmailHTML(blocks, templateStyles, {
          pollTracking: effectiveWorkspaceId && campaignId ? {
            workspaceId: effectiveWorkspaceId,
            campaignId: campaignId
          } : undefined,
          branding: brandingOptions,
        address: formattedAddress,
        hideBranding: effectiveCanRemoveBranding
        });
        if (!htmlContent.trim()) {
          setStatusTone('error');
          setStatus('Email content cannot be empty.');
          return;
        }

        setValidationErrorBlocks([]); // Clear previous errors
        setValidationErrorBlockIds([]); // Clear previous IDs

        if (mode === 'next' && !ignorePlaceholders) {
          const validation = hasDefaultPlaceholders(blocks);
          if (validation.hasPlaceholders) {
            setValidationErrorBlocks(validation.blocksWithPlaceholders);
            setValidationErrorBlockIds(validation.blockIdsWithPlaceholders);
            if (validation.blockIdsWithPlaceholders.length > 0) {
              setSelectedBlockId(validation.blockIdsWithPlaceholders[0]);
            }
            return;
          }
        }

        const payload = buildTemplate(htmlContent);
        setSavingMode(mode);

        if (mode === 'save' || !onSaveAndNext) {
          await Promise.resolve(onSave(payload));
          if (onManualSaveComplete) {
            await Promise.resolve(onManualSaveComplete());
          } else {
            const isInAdminContext = window.location.pathname.includes('/admin/control-panel');
            if (!isInAdminContext) {
              if (currentWorkspace?.id) {
                navigate(`/workspace/${currentWorkspace.id}/campaigns`);
              } else {
                navigate('/account/settings');
              }
            }
          }
        } else {
          await Promise.resolve(onSaveAndNext(payload));
        }

        const now = new Date();
        setLastSavedAt(now);
        const snapshot = computeTemplateSnapshot();
        lastSavedSnapshotRef.current = snapshot;
        setAutoSaveStatus('saved');
        setAutoSaveError(null);
      } catch (err) {
        console.error('Failed to save template:', err);
        setStatusTone('error');
        setStatus('Failed to save template. Please try again.');
        enqueueAutoSave(computeTemplateSnapshot(), AUTOSAVE_DEBOUNCE_MS);
      } finally {
        setSavingMode(null);
      }
    },
    [blocks, template, buildTemplate, onSave, onSaveAndNext, savingMode, navigate, currentWorkspace, computeTemplateSnapshot, enqueueAutoSave, effectiveWorkspaceId, campaignId, brandingOptions, templateStyles, formattedAddress, effectiveCanRemoveBranding]
  );

  const handleExportHtml = useCallback(() => {
    try {
      const htmlContent = generateEmailHTML(blocks, templateStyles, {
        pollTracking: effectiveWorkspaceId && campaignId ? {
          workspaceId: effectiveWorkspaceId,
          campaignId: campaignId
        } : undefined
      });
      if (!htmlContent.trim()) {
        setStatusTone('error');
        setStatus('Nothing to export yet.');
        return;
      }
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const baseName =
        (templateName || 'email-template').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'email-template';
      anchor.href = url;
      anchor.download = `${baseName}.html`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      setStatusTone('success');
      setStatus('HTML exported.');
    } catch (err) {
      console.error('Failed to export HTML:', err);
      setStatusTone('error');
      setStatus('Failed to export HTML.');
    } finally {
      setSaveMenuOpen(false);
    }
  }, [blocks, template, templateName, effectiveWorkspaceId, campaignId, brandingOptions, templateStyles]);

  const handleExportTemplate = useCallback(() => {
    try {
      const templateData = {
        name: templateName,
        subject: templateSubject,
        blocks: blocks,
        styles: templateStyles,
        version: '2.0',
        exportedAt: new Date().toISOString(),
      };
      
      const fileName = `scubamail-template-${templateName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.json`;
      const jsonString = JSON.stringify(templateData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showSuccess('Template exported successfully');
    } catch (error) {
      console.error('Failed to export template:', error);
      showError('Failed to export template');
    }
  }, [templateName, templateSubject, blocks, templateStyles, showSuccess, showError]);

  const handleImportTemplate = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);
        
        // Basic validation
        if (!data.blocks || !Array.isArray(data.blocks)) {
          throw new Error('Invalid template format: Missing blocks');
        }
        
        setImportPendingData(data);
        setShowImportConfirmModal(true);
      } catch (error) {
        console.error('Failed to import template:', error);
        showError('Failed to import template: Invalid file format');
      }
    };
    reader.readAsText(file);
  }, [showError]);

  const confirmImportTemplate = () => {
    if (!importPendingData) return;
    
    setBlocks(importPendingData.blocks);
    if (importPendingData.styles) setTemplateStyles(importPendingData.styles);
    if (importPendingData.name) setTemplateName(importPendingData.name);
    if (importPendingData.subject) setTemplateSubject(importPendingData.subject);
    
    setSelectedBlockId(null);
    setShowImportConfirmModal(false);
    setImportPendingData(null);
    showSuccess('Template imported successfully');
  };

  const handleCampaignNameSave = useCallback(async () => {
    const trimmed = editedCampaignName.trim();
    if (!trimmed || trimmed === campaignName) {
      setEditedCampaignName(campaignName || '');
      setIsEditingCampaignName(false);
      return;
    }
    try {
      if (campaignId) {
        const { updateCampaign } = await import('../../services/campaignService');
        await updateCampaign(campaignId, { name: trimmed });
      }
      onCampaignNameUpdate?.(trimmed);
      setStatusTone('success');
      setStatus('Campaign name updated.');
    } catch (err) {
      console.error('Failed to update campaign name:', err);
      setStatusTone('error');
      setStatus('Failed to update campaign name.');
      setEditedCampaignName(campaignName || '');
    } finally {
      setIsEditingCampaignName(false);
    }
  }, [campaignId, campaignName, editedCampaignName, onCampaignNameUpdate]);

  const handleCampaignNameKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleCampaignNameSave();
      } else if (e.key === 'Escape') {
        setEditedCampaignName(campaignName || '');
        setIsEditingCampaignName(false);
      }
    },
    [campaignName, handleCampaignNameSave]
  );

  const canvaGuideWidthPx = useMemo(
    () => parseEmailContainerWidthToPx(templateStyles.containerWidth),
    [templateStyles.containerWidth]
  );

  return (
    <>
      {isLoading && <BuilderLoadingScreen onComplete={() => setIsLoading(false)} />}
      <CanvaImportGuideProvider
      recommendedWidthPx={canvaGuideWidthPx}
      userId={currentUser?.uid ?? null}
      workspaceId={effectiveWorkspaceId}
    >
    <div className="fixed inset-0 h-screen w-screen flex flex-col text-slate-900 overflow-hidden" style={{ backgroundColor: '#EBEDF0' }}>
      {/* Header — transparent on the warm shell, no divider */}
      <header className="flex-shrink-0 px-4 sm:px-6 py-3 z-50 relative" style={{ backgroundColor: 'transparent' }}>
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0">
          {/* Left side - Back Button and Template Name */}
          <div className="flex items-center space-x-4 lg:space-x-6 flex-shrink-0">
            {/* Back Button */}
            <button
              onClick={onCancel}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-black/5 rounded-md transition-colors"
              title="Go Back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>

            {/* Template Name */}
            <div className="flex items-center space-x-2 max-w-xs lg:max-w-md">
              <input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="px-2.5 py-1 bg-transparent border border-black/[0.1] text-slate-700 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400/40 min-w-0 font-medium"
                placeholder="Template name"
              />
            </div>
          </div>

          {/* Right side - Save Actions */}
          <div className="flex items-center space-x-2 sm:space-x-3 ml-auto flex-shrink-0">
            <div className="flex flex-col items-end space-y-1 mr-3">
              {autoSaveStatus !== 'idle' && (
                <div
                  className={`text-sm flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${autoSaveStatus === 'error'
                    ? 'bg-red-100 text-red-700 border border-red-200'
                    : autoSaveStatus === 'pending'
                      ? 'bg-amber-100 text-amber-800 border border-amber-200'
                      : autoSaveStatus === 'saving'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'bg-slate-100 text-slate-700 border border-slate-200'
                    }`}
                >
                  {/* Status Icon */}
                  {autoSaveStatus === 'pending' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" strokeDasharray="30 70" className="animate-spin origin-center" style={{ transformOrigin: '50% 50%' }} />
                    </svg>
                  )}
                  {autoSaveStatus === 'saving' && (
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {autoSaveStatus === 'error' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {/* Status Text */}
                  {autoSaveStatus === 'pending' && <span>Unsaved changes</span>}
                  {autoSaveStatus === 'saving' && <span>Saving...</span>}
                  {autoSaveStatus === 'saved' && <span>Saved</span>}
                  {autoSaveStatus === 'error' && (
                    <>
                      <span>{autoSaveError || 'Save failed'}</span>
                      <button
                        type="button"
                        onClick={handleAutoSaveRetry}
                        className="underline decoration-dotted underline-offset-2 hover:text-red-200 transition ml-1"
                      >
                        Retry
                      </button>
                    </>
                  )}
                </div>
              )}
              {status && (
                <span
                  className={`text-xs px-2 py-1 rounded ${statusTone === 'error'
                    ? 'bg-red-900/50 text-red-300 border border-red-700'
                    : statusTone === 'success'
                      ? 'bg-green-900/50 text-green-300 border border-green-700'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}
                >
                  {status}
                </span>
              )}
            </div>
            <button
              onClick={() => handleSave('save')}
              disabled={Boolean(savingMode)}
              className={`px-4 sm:px-6 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-sm ${savingMode ? 'bg-slate-400 cursor-not-allowed opacity-70' : 'bg-slate-700 hover:bg-slate-800'
                }`}
            >
              {savingMode === 'save' ? 'Saving…' : 'Save and Close'}
            </button>
            {onSaveAndNext && (
              <div className="relative" ref={saveMenuRef}>
                <div className="flex rounded-lg overflow-hidden shadow-sm">
                  <button
                    onClick={() => handleSave('next')}
                    disabled={Boolean(savingMode)}
                    className="px-4 sm:px-6 py-2 text-sm font-semibold text-slate-900 bg-white hover:bg-slate-100 transition-all duration-200"
                  >
                    {savingMode === 'next' ? 'Saving…' : 'Save and Next'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSaveMenuOpen((prev) => !prev)}
                    disabled={Boolean(savingMode)}
                    aria-haspopup="menu"
                    aria-expanded={saveMenuOpen}
                    className={`px-3 flex items-center justify-center text-slate-900 border-l border-slate-200 transition-colors ${savingMode ? 'bg-slate-50 cursor-not-allowed opacity-70' : 'bg-white hover:bg-slate-100'
                      }`}
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${saveMenuOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                {saveMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-[100]">
                    <button
                      onClick={handleExportHtml}
                      disabled={Boolean(savingMode)}
                      className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-between"
                    >
                      Export HTML
                      <span className="text-xs text-slate-400">.html</span>
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const htmlContent = generateEmailHTML(blocks, templateStyles, {
                            branding: brandingOptions,
        address: formattedAddress,
        hideBranding: effectiveCanRemoveBranding
                          });
                          if (!htmlContent.trim()) {
                            setStatusTone('error');
                            setStatus('Nothing to copy yet.');
                            return;
                          }
                          await navigator.clipboard.writeText(htmlContent);
                          setStatusTone('success');
                          setStatus('HTML copied to clipboard!');
                        } catch (err) {
                          console.error('Failed to copy HTML:', err);
                          setStatusTone('error');
                          setStatus('Failed to copy HTML.');
                        } finally {
                          setSaveMenuOpen(false);
                        }
                      }}
                      disabled={Boolean(savingMode)}
                      className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-between border-t border-slate-100"
                    >
                      Copy HTML
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleSave('save')}
                      disabled={Boolean(savingMode)}
                      className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                    >
                      {savingMode === 'save' ? 'Saving…' : 'Save as Template'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Premium Validation Error Notification Pop-up */}
      {validationErrorBlocks.length > 0 && (
        <div className="fixed top-20 right-6 z-[100] animate-in fade-in slide-in-from-right-4 duration-300 w-full max-w-[500px]">
          <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex items-stretch justify-between p-1.5 border border-rose-100 ring-1 ring-black/5">
            <div className="flex items-center gap-4 px-3 py-2.5">
              <div className="bg-rose-50 p-2.5 rounded-xl flex-shrink-0">
                <svg className="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="min-w-0 pr-4">
                <h3 className="text-sm font-bold text-slate-900 leading-tight">Personalize your email</h3>
                <p className="text-[13px] text-slate-500 mt-0.5 leading-snug truncate">
                  Some blocks still have default content
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-1 bg-slate-50/80 rounded-xl p-1.5 ml-2">
              <button
                onClick={() => {
                  setValidationErrorBlocks([]);
                  handleSave('next', true); // Skip validation and proceed
                }}
                className="px-2.5 py-1.5 text-[11px] font-bold text-white bg-slate-900 hover:bg-black rounded-lg transition-all shadow-sm active:scale-95 whitespace-nowrap"
              >
                Proceed anyway
              </button>
              <button 
                onClick={() => {
                  setValidationErrorBlocks([]);
                  setValidationErrorBlockIds([]);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-slate-200"
                title="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDragId(null)}
      >
        <div className="flex-1 min-h-0 flex overflow-hidden relative">
          {/* Canvas — shrinks to make room for the properties panel */}
          <main className={`flex-1 min-w-0 flex flex-col overflow-hidden transition-all duration-300 ${!isPreviewMode ? 'mr-[320px]' : ''}`}>
            {/* Secondary Bar — centered toolbar */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-1.5 flex items-center" style={{ backgroundColor: 'transparent' }}>
              {/* Left: Add button */}
              <div className="flex items-center flex-shrink-0">
                {!isPreviewMode && (
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (isAddPanelOpen && addPanelCategory !== null) {
                          setAddPanelCategory(null);
                        } else if (isAddPanelOpen) {
                          setIsAddPanelOpen(false);
                          setAddPanelCategory(null);
                        } else {
                          setIsAddPanelOpen(true);
                          setAddPanelCategory(null);
                        }
                      }}
                      className={`add-btn group relative flex items-center gap-2.5 px-5 py-2.5 rounded-2xl text-[14px] font-semibold overflow-hidden
                        transition-all duration-200 active:scale-[0.92] select-none
                        ${isAddPanelOpen ? 'text-white shadow-xl' : 'text-slate-800 shadow-md hover:shadow-xl hover:scale-[1.05]'}`}
                      style={{
                        letterSpacing: '0.01em',
                        background: isAddPanelOpen ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : undefined,
                        border: isAddPanelOpen ? 'none' : '1.5px solid rgba(0,0,0,0.10)',
                        backgroundColor: isAddPanelOpen ? undefined : '#ffffff',
                      }}
                    >
                      {!isAddPanelOpen && (
                        <span aria-hidden="true" className="add-btn-video pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      )}
                      {!isAddPanelOpen && (
                        <span aria-hidden="true" className="add-btn-pulse pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 w-7 h-7 rounded-full opacity-0 group-hover:opacity-100" />
                      )}
                      <span className={`relative z-10 flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 transition-all duration-300 ${isAddPanelOpen ? 'bg-white/20 rotate-45' : 'bg-slate-100 group-hover:bg-white group-hover:shadow-sm'}`}>
                        <svg className={`w-3 h-3 transition-all duration-300 ${isAddPanelOpen ? '' : 'group-hover:text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                        </svg>
                      </span>
                      <span className={`relative z-10 transition-all duration-300 ${!isAddPanelOpen ? 'group-hover:text-indigo-700' : ''}`}>Add</span>
                      <style>{`
                        @keyframes addBtnFlow { 0%{background-position:0% 50%} 25%{background-position:100% 0%} 50%{background-position:200% 50%} 75%{background-position:100% 100%} 100%{background-position:0% 50%} }
                        .add-btn-video { background: linear-gradient(135deg,#e0e7ff 0%,#c7d2fe 15%,#ddd6fe 30%,#fae8ff 45%,#e0f2fe 60%,#d1fae5 75%,#fef9c3 90%,#e0e7ff 100%); background-size:400% 400%; animation:addBtnFlow 2.4s ease infinite; }
                        @keyframes addBtnPulse { 0%,100%{transform:translateY(-50%) scale(0.8);opacity:0} 50%{transform:translateY(-50%) scale(1.6);opacity:0.25} }
                        .add-btn-pulse { background:radial-gradient(circle,#6366f1 0%,transparent 70%); animation:addBtnPulse 1.6s ease-in-out infinite; }
                        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
                      `}</style>
                    </button>

                    {/* Category picker dropdown */}
                    {isAddPanelOpen && addPanelCategory === null && (
                      <div className="absolute left-0 top-[calc(100%+8px)] z-[60]" style={{ width: '240px' }} onMouseDown={(e) => e.stopPropagation()}>
                        <div className="flex flex-col gap-2 p-1">
                          {([
                            { key: 'basic' as const, label: 'Elements', subtitle: 'Text, images & buttons', thumbnail: (<svg width="56" height="48" viewBox="0 0 56 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="56" height="48" rx="8" fill="#F0F4FF"/><rect x="9" y="13" width="34" height="26" rx="4" fill="#C7D2FE" opacity="0.6"/><rect x="7" y="10" width="34" height="26" rx="4" fill="#A5B4FC" opacity="0.7"/><rect x="5" y="7" width="34" height="26" rx="4" fill="white" stroke="#C7D2FE" strokeWidth="0.75"/><rect x="8" y="10" width="14" height="10" rx="2" fill="#E0E7FF"/><circle cx="12" cy="14" r="2.5" fill="#818CF8"/><path d="M8 20 l4-3 3 2.5 3-4 4 4.5H8z" fill="#A5B4FC"/><rect x="24" y="11" width="12" height="2" rx="1" fill="#C7D2FE"/><rect x="24" y="15" width="9" height="1.5" rx="0.75" fill="#E0E7FF"/><rect x="24" y="18" width="11" height="1.5" rx="0.75" fill="#E0E7FF"/><rect x="8" y="27" width="28" height="4" rx="2" fill="#6366F1"/><rect x="16" y="28.5" width="12" height="1" rx="0.5" fill="white" opacity="0.9"/><rect x="34" y="28" width="16" height="14" rx="4" fill="#6366F1"/><path d="M39 35h6M39 32.5h4M39 37.5h5" stroke="white" strokeWidth="1.2" strokeLinecap="round"/></svg>) },
                            { key: 'sections' as const, label: 'Sections', subtitle: 'Hero, features & footer', thumbnail: (<svg width="56" height="48" viewBox="0 0 56 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="56" height="48" rx="8" fill="#ECFDF5"/><rect x="9" y="12" width="36" height="28" rx="4" fill="#A7F3D0" opacity="0.5"/><rect x="6" y="8" width="36" height="28" rx="4" fill="white" stroke="#6EE7B7" strokeWidth="0.75"/><rect x="6" y="8" width="36" height="12" rx="4" fill="#D1FAE5"/><rect x="10" y="12" width="14" height="2" rx="1" fill="#059669"/><rect x="10" y="15.5" width="10" height="1.2" rx="0.6" fill="#6EE7B7"/><rect x="30" y="9.5" width="10" height="9" rx="2" fill="#A7F3D0"/><circle cx="34" cy="12.5" r="2" fill="#34D399"/><rect x="9" y="23" width="30" height="2" rx="1" fill="#D1FAE5"/><rect x="9" y="27" width="22" height="2" rx="1" fill="#ECFDF5"/><rect x="9" y="31" width="26" height="2" rx="1" fill="#ECFDF5"/><rect x="34" y="29" width="16" height="14" rx="4" fill="#059669"/><rect x="37" y="33" width="10" height="1.2" rx="0.6" fill="white" opacity="0.9"/><rect x="37" y="36" width="7" height="1.2" rx="0.6" fill="white" opacity="0.6"/><rect x="37" y="31" width="10" height="1.2" rx="0.6" fill="#A7F3D0"/></svg>) },
                            { key: 'saved' as const, label: 'Saved', subtitle: 'Your saved blocks', thumbnail: (<svg width="56" height="48" viewBox="0 0 56 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="56" height="48" rx="8" fill="#FFF7ED"/><rect x="12" y="14" width="30" height="22" rx="4" fill="#FED7AA" opacity="0.5"/><rect x="9" y="11" width="30" height="22" rx="4" fill="#FDBA74" opacity="0.6"/><rect x="6" y="8" width="30" height="22" rx="4" fill="white" stroke="#FED7AA" strokeWidth="0.75"/><rect x="9" y="11" width="12" height="8" rx="2" fill="#FFF7ED"/><circle cx="13" cy="14" r="2" fill="#FB923C"/><rect x="23" y="12" width="10" height="1.5" rx="0.75" fill="#FED7AA"/><rect x="23" y="15.5" width="7" height="1.2" rx="0.6" fill="#FFE4CA"/><rect x="23" y="18.5" width="9" height="1.2" rx="0.6" fill="#FFE4CA"/><rect x="9" y="22" width="24" height="6" rx="2" fill="#FFF7ED"/><rect x="11" y="24.5" width="8" height="1.2" rx="0.6" fill="#FED7AA"/><rect x="21" y="24.5" width="6" height="1.2" rx="0.6" fill="#FFE4CA"/><rect x="32" y="27" width="16" height="16" rx="4" fill="#EA580C"/><path d="M38 31 v8 l2.5-2 2.5 2 V31 a1 1 0 00-1-1H39 a1 1 0 00-1 1z" fill="white" opacity="0.9"/></svg>) },
                          ] as const).map((item, i) => (
                            <button key={item.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setAddPanelCategory(item.key)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-150 group rounded-2xl hover:shadow-md active:scale-[0.98]"
                              style={{
                                animation: `fadeSlideIn 0.22s ease-out ${i * 65}ms both`,
                                backgroundColor: '#ffffff',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.05)',
                              }}>
                              <div className="flex-shrink-0 rounded-xl overflow-hidden transition-transform duration-150 group-hover:scale-[1.04] group-hover:shadow-md" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.10))' }}>{item.thumbnail}</div>
                              <div className="min-w-0 flex-1"><p className="text-[13px] font-semibold text-slate-800 leading-tight">{item.label}</p><p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{item.subtitle}</p></div>
                              <svg className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Center: design controls — absolutely centered */}
              {!isPreviewMode && (
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-0 select-none" style={{ zIndex: 50, isolation: 'isolate' }}>
                  {/* helper: flat toolbar button */}
                  {/* Colors */}
                  <div className="relative">
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={(e) => openPopover('colors', e.currentTarget)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium transition-colors ${designPopover === 'colors' ? 'text-slate-950' : 'text-slate-700 hover:text-slate-950'}`}>
                      <span className="flex items-center gap-0.5">
                        <span className="w-3 h-3 rounded-full border border-black/[0.15] inline-block" style={{ backgroundColor: templateStyles.backgroundColor || '#fff' }} />
                        <span className="w-3 h-3 rounded-full border border-black/[0.15] inline-block -ml-1" style={{ backgroundColor: templateStyles.contentBackgroundColor || '#fff' }} />
                        <span className="w-3 h-3 rounded-full border border-black/[0.15] inline-block -ml-1" style={{ backgroundColor: templateStyles.textColor || '#111827' }} />
                      </span>
                      Change Color
                    </button>
                    {designPopover === 'colors' && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+6px)] z-[500] w-52 rounded-xl shadow-xl border border-black/[0.08] p-3 space-y-2.5 animate-in fade-in slide-in-from-top-1 duration-150" style={{ backgroundColor: '#ffffff' }}>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Change Color</p>
                        {[
                          { label: 'Page BG', key: 'backgroundColor' as const, val: templateStyles.backgroundColor || '#ffffff' },
                          { label: 'Content BG', key: 'contentBackgroundColor' as const, val: templateStyles.contentBackgroundColor || '#ffffff' },
                          { label: 'Text', key: 'textColor' as const, val: templateStyles.textColor || '#111827' },
                        ].map(({ label, key, val }) => (
                          <label key={key} className="flex items-center justify-between gap-2 cursor-pointer group">
                            <span className="text-[12px] text-slate-600">{label}</span>
                            <div className="relative flex-shrink-0">
                              <span className="w-7 h-7 rounded-lg border border-black/[0.12] block shadow-sm group-hover:scale-105 transition-transform" style={{ backgroundColor: val }} />
                              <input type="color" value={val} onChange={(e) => handleUpdateTemplateStyles({ [key]: e.target.value })} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <span className="w-px h-4 bg-black/[0.12] mx-0.5" />

                  {/* Font */}
                  <div className="relative">
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={(e) => openPopover('font', e.currentTarget)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium transition-colors max-w-[140px] ${designPopover === 'font' ? 'text-slate-950' : 'text-slate-700 hover:text-slate-950'}`}>
                      <span className="text-[13px] font-bold flex-shrink-0" style={{ fontFamily: templateStyles.fontFamily }}>Aa</span>
                      <span className="truncate">{(templateStyles.fontFamily || 'Arial').split(',')[0]}</span>
                    </button>
                    {designPopover === 'font' && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+6px)] z-[500] w-52 rounded-xl shadow-xl border border-black/[0.08] animate-in fade-in slide-in-from-top-1 duration-150 overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest px-3 pt-3 pb-1">Font</p>
                        <div className="max-h-60 overflow-y-auto custom-scrollbar pb-2">
                          {FONT_FAMILIES.map((font) => (
                            <button key={font.value} type="button"
                              onClick={() => { handleUpdateTemplateStyles({ fontFamily: font.value }); setDesignPopover(null); }}
                              className={`w-full text-left px-3 py-2 text-[13px] transition-colors flex items-center justify-between ${templateStyles.fontFamily === font.value ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                              style={{ fontFamily: font.value }}>
                              {font.label}
                              {templateStyles.fontFamily === font.value && <span className="w-1.5 h-1.5 rounded-full bg-slate-900 flex-shrink-0" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <span className="w-px h-4 bg-black/[0.12] mx-0.5" />

                  {/* Footer */}
                  <div className="relative">
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={(e) => openPopover('footer', e.currentTarget)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium transition-colors ${designPopover === 'footer' ? 'text-slate-950' : 'text-slate-700 hover:text-slate-950'}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0l-3.75-3.75M17.25 21l3.75-3.75" /></svg>
                      Footer Settings
                    </button>
                    {designPopover === 'footer' && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+6px)] z-[500] w-72 rounded-xl shadow-xl border border-black/[0.08] p-3 animate-in fade-in slide-in-from-top-1 duration-150" style={{ backgroundColor: '#ffffff' }}>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Footer Settings</p>
                        <div className="space-y-1.5">
                          {COMPLIANCE_FOOTER_VARIANTS.map((item) => {
                            const selected = normalizeComplianceFooterVariant(templateStyles.complianceFooterVariant) === item.id;
                            return (
                              <button key={item.id} type="button"
                                onClick={() => handleUpdateTemplateStyles({ complianceFooterVariant: item.id as ComplianceFooterVariant })}
                                className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${selected ? 'border-slate-900 bg-slate-900/5' : 'border-black/[0.07] bg-slate-50/50 hover:bg-white hover:border-black/[0.15]'}`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-[12px] font-semibold text-slate-700">{item.label}</span>
                                  {selected && <span className="w-1.5 h-1.5 rounded-full bg-slate-900" />}
                                </div>
                                <p className="text-[10px] text-slate-400 mt-0.5">{item.description}</p>
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3 pt-3 border-t border-black/[0.06]">
                          <label className="flex items-center justify-between gap-2 cursor-pointer group">
                            <span className="text-[11px] text-slate-500">Footer text color</span>
                            <div className="relative flex-shrink-0">
                              <span className="w-6 h-6 rounded-md border border-black/[0.12] block" style={{ backgroundColor: resolveComplianceFooterTextColor(templateStyles) }} />
                              <input type="color" value={resolveComplianceFooterTextColor(templateStyles)} onChange={(e) => handleUpdateTemplateStyles({ complianceFooterTextColor: e.target.value })} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  <span className="w-px h-4 bg-black/[0.12] mx-0.5" />

                  {/* Template Management popover */}
                  <div className="relative">
                    <button type="button" onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => openPopover('template', e.currentTarget)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium transition-colors ${designPopover === 'template' ? 'text-slate-950' : 'text-slate-700 hover:text-slate-950'}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                      Template Management
                    </button>
                    {designPopover === 'template' && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+6px)] z-[500] w-48 rounded-xl shadow-xl border border-black/[0.08] py-1.5 animate-in fade-in slide-in-from-top-1 duration-150" style={{ backgroundColor: '#ffffff' }}>
                        <button type="button" onClick={() => { handleExportTemplate(); setDesignPopover(null); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-slate-700 hover:text-slate-950 hover:bg-slate-50 transition-colors">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                          Export
                        </button>
                        <button type="button" onClick={() => { toolbarImportRef.current?.click(); setDesignPopover(null); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-slate-700 hover:text-slate-950 hover:bg-slate-50 transition-colors">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
                          Import
                        </button>
                      </div>
                    )}
                  </div>
                  <input ref={toolbarImportRef} type="file" accept=".json" className="hidden"
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleImportTemplate(file); e.target.value = ''; }} />

                  {/* Backdrop */}
                  {designPopover && <div className="fixed inset-0 z-[199]" onClick={() => { setDesignPopover(null); setPopoverPos(null); }} />}
                </div>
              )}

              {/* Right: Preview */}
              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                <button type="button" onClick={() => setIsPreviewModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-slate-700 hover:text-slate-950 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview
                </button>
              </div>
            </div>
            <div
              className={`flex-1 overflow-auto pb-8 pt-4 transition-colors duration-300 pointer-events-auto no-scrollbar hide-scrollbar ${previewDevice === 'desktop' ? 'pl-24 pr-8' : 'px-8'}`}
              style={{ 
                overflowX: 'auto',
                backgroundColor: '#EBEDF0'
              }}
              onClick={() => {
                setSelectedBlockId(null);
                setSelectedColumnBlock(null);
                setShowGlobalSettings(true);
              }}
            >
              {/* Mock browser chrome wraps the email canvas */}
              <div
                className="mx-auto transition-all duration-300"
                style={{
                  width: previewDevice === 'mobile' ? '375px' : previewDevice === 'tablet' ? '768px' : '100%',
                  maxWidth: '100%',
                  borderRadius: '10px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
                  overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Browser chrome bar */}
                <div
                  style={{ backgroundColor: '#EBEBEB', borderBottom: '1px solid rgba(0,0,0,0.08)' }}
                  className="flex items-center gap-1.5 px-3 py-2 flex-shrink-0"
                >
                  {/* Traffic lights */}
                  <span className="w-3 h-3 rounded-full bg-[#FF5F57] opacity-80 flex-shrink-0" />
                  <span className="w-3 h-3 rounded-full bg-[#FFBD2E] opacity-80 flex-shrink-0" />
                  <span className="w-3 h-3 rounded-full bg-[#28C840] opacity-80 flex-shrink-0" />
                  {/* URL pill removed */}
                  <div className="flex-1" />
                  {/* Undo / Redo / Shortcuts / Device switcher */}
                  {!isPreviewMode && (
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button type="button" onClick={handleUndo} disabled={!canUndo} title="Undo (⌘Z)"
                        className={`flex items-center justify-center w-6 h-6 rounded transition-all ${canUndo ? 'text-slate-500 hover:bg-black/[0.08] hover:text-slate-800' : 'text-slate-300 cursor-not-allowed'}`}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
                      </button>
                      <button type="button" onClick={handleRedo} disabled={!canRedo} title="Redo (⌘⇧Z)"
                        className={`flex items-center justify-center w-6 h-6 rounded transition-all ${canRedo ? 'text-slate-500 hover:bg-black/[0.08] hover:text-slate-800' : 'text-slate-300 cursor-not-allowed'}`}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" /></svg>
                      </button>
                      <div className="w-px h-3.5 bg-black/[0.12] mx-0.5" />
                      <button type="button" onClick={() => setIsShortcutsModalOpen(true)} title="Keyboard Shortcuts"
                        className="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-black/[0.08] hover:text-slate-700 transition-all">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </button>
                      <div className="w-px h-3.5 bg-black/[0.12] mx-0.5" />
                      <button type="button" onClick={() => { setPreviewDevice('desktop'); handleUpdateTemplateStyles({ containerWidth: '600px' }); }} title="Desktop"
                        className={`flex items-center justify-center w-6 h-6 rounded transition-all ${previewDevice === 'desktop' ? 'bg-black/[0.10] text-slate-800' : 'text-slate-400 hover:bg-black/[0.08] hover:text-slate-700'}`}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      </button>
                      <button type="button" onClick={() => { setPreviewDevice('tablet'); handleUpdateTemplateStyles({ containerWidth: '480px' }); }} title="Tablet"
                        className={`flex items-center justify-center w-6 h-6 rounded transition-all ${previewDevice === 'tablet' ? 'bg-black/[0.10] text-slate-800' : 'text-slate-400 hover:bg-black/[0.08] hover:text-slate-700'}`}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                      </button>
                      <button type="button" onClick={() => { setPreviewDevice('mobile'); handleUpdateTemplateStyles({ containerWidth: '375px' }); }} title="Mobile"
                        className={`flex items-center justify-center w-6 h-6 rounded transition-all ${previewDevice === 'mobile' ? 'bg-black/[0.10] text-slate-800' : 'text-slate-400 hover:bg-black/[0.08] hover:text-slate-700'}`}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                      </button>
                    </div>
                  )}
                </div>

                {/* Actual email canvas — full width with page bg, email content centered */}
                <div
                  className={`email-canvas-wrapper transition-all duration-300 ${previewDevice === 'mobile' ? 'email-canvas-mobile' : previewDevice === 'tablet' ? 'email-canvas-tablet' : ''}`}
                  style={{
                    minHeight: blocks.length === 0 ? '260px' : '400px',
                    backgroundColor: templateStyles.backgroundColor || '#f5f5f5',
                    color: templateStyles.textColor || '#111827',
                    fontFamily: templateStyles.fontFamily || 'Arial, sans-serif',
                    paddingTop: '20px',
                    paddingBottom: '20px',
                  }}
                >
                  {/* Inner email content constrained to container width */}
                  <div
                    style={{
                      width: previewDevice === 'desktop' ? (templateStyles.containerWidth || '600px') : '100%',
                      maxWidth: '100%',
                      margin: '0 auto',
                      backgroundColor: templateStyles.contentBackgroundColor || '#ffffff',
                    }}
                  >
                  <style>{FOOTER_BUILDER_CSS}</style>
                  {previewDevice !== 'desktop' && (
                    <style>{FOOTER_BUILDER_MOBILE_CSS}</style>
                  )}
                  <SortableContext
                    items={blocks.map((b) => b.id)}
                    strategy={verticalListSortingStrategy}
                    key={`canvas-${previewDevice}`}
                  >
                    <CanvasDropZone blocks={blocks} activeDragId={activeDragId}>
                      {blocks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-52 text-slate-400">
                          <svg
                            className="w-16 h-16 mb-4 opacity-50"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          <p className="text-lg font-medium">Start building your email</p>
                          <p className="text-sm mt-1">Drag blocks from the left sidebar</p>
                        </div>
                      ) : (
                        (() => {
                          // Group blocks by section
                          const groupedBlocks: { section: string | undefined; blocks: EmailBlockData[] }[] = [];
                          let currentSection: string | undefined = undefined;
                          let currentGroup: EmailBlockData[] = [];

                          blocks.forEach((block, index) => {
                            const blockSection = block.section || 'body';

                            if (blockSection !== currentSection) {
                              // Save previous group if it has blocks
                              if (currentGroup.length > 0) {
                                groupedBlocks.push({ section: currentSection, blocks: currentGroup });
                              }
                              // Start new group
                              currentSection = blockSection;
                              currentGroup = [block];
                            } else {
                              currentGroup.push(block);
                            }

                            // If this is the last block, save the current group
                            if (index === blocks.length - 1) {
                              groupedBlocks.push({ section: currentSection, blocks: currentGroup });
                            }
                          });

                          const isDraggingFromPalette = activeDragId && !blocks.some(b => b.id === activeDragId);

                          return (
                            <>
                              {/* Drop zone at the beginning */}
                              {blocks.length > 0 && (
                                <BlockDropZone
                                  id="drop-zone-0"
                                  index={0}
                                  activeDragId={activeDragId}
                                  isFromPalette={!!isDraggingFromPalette}
                                />
                              )}

                              {groupedBlocks.map((group, groupIndex) => (
                                <div key={`section-${groupIndex}`} className={`relative ${group.blocks.some(b => b.id === selectedBlockId) ? 'z-[40]' : ''}`}>
                                  {/* Section Blocks */}
                                  <div className={group.section === 'header' ? 'bg-blue-50/30' :
                                    group.section === 'footer' ? 'bg-purple-50/30' :
                                      ''}>
                                    {group.blocks.map((block, blockIndex) => {
                                      const blockIndexInAll = blocks.findIndex((b) => b.id === block.id);
                                      const isFirst = blockIndexInAll === 0;
                                      const isLast = blockIndexInAll === blocks.length - 1;
                                      return (
                                        <React.Fragment key={block.id}>
                                          {/* Drop zone before block (except first) */}
                                          {!isFirst && (
                                            <BlockDropZone
                                              id={`drop-zone-${blockIndexInAll}`}
                                              index={blockIndexInAll}
                                              activeDragId={activeDragId}
                                              isFromPalette={!!isDraggingFromPalette}
                                            />
                                          )}
                                          <EmailBlock
                                            block={block}
                                            isSelected={selectedBlockId === block.id}
                                            hasValidationError={validationErrorBlockIds.includes(block.id)}
                                            isPreview={isPreviewMode}
                                            onSelect={() => handleBlockSelect(block.id)}
                                            onUpdate={handleBlockUpdate}
                                            onDelete={handleBlockDelete}
                                            onDuplicate={handleBlockDuplicate}
                                            onMoveUp={handleBlockMoveUp}
                                            onMoveDown={handleBlockMoveDown}
                                            canMoveUp={!isFirst}
                                            canMoveDown={!isLast}
                                            onInsertBlock={handleInsertBlock}
                                            blockIndex={blockIndexInAll}
                                            onSelectColumnBlock={handleSelectColumnBlock}
                                            onDeleteColumnBlock={handleDeleteColumnBlock}
                                            selectedColumnBlock={selectedColumnBlock}
                                            onUpdateColumnBlock={handleUpdateColumnBlock}
                                            isMobile={previewDevice !== 'desktop'}
                                            isLocked={!!block.isScubamailBranding && !effectiveCanRemoveBranding}
                                            onSaveBlock={(block, name) => {
                                              savedBlocksService.save(block, name);
                                              // Trigger a custom event to refresh saved blocks in BlockPalette
                                              window.dispatchEvent(new Event('savedBlocksUpdated'));
                                            }}
                                            globalFontFamily={templateStyles.fontFamily}
                                          />
                                          {/* Drop zone after last block */}
                                          {isLast && (
                                            <BlockDropZone
                                              id={`drop-zone-${blocks.length}`}
                                              index={blocks.length}
                                              activeDragId={activeDragId}
                                              isFromPalette={!!isDraggingFromPalette}
                                            />
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </>
                          );
                        })()
                      )}
                    </CanvasDropZone>
                  </SortableContext>
                  <DragOverlay>
                    {activeDragId ? (
                      <div className="bg-slate-900 px-3 py-2 rounded-lg shadow-lg pointer-events-none border border-slate-700">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="text-xs font-medium text-white">Drop here</span>
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                  </div> {/* Closes inner email content div */}
                </div> {/* Closes email-canvas-wrapper */}

                <ComplianceFooter
                  variant={templateStyles.complianceFooterVariant}
                  formattedAddress={formattedAddress}
                  textColor={resolveComplianceFooterTextColor(templateStyles)}
                  showBadge={!effectiveCanRemoveBranding || templateStyles.showBadge !== false}
                  workspaceId={effectiveWorkspaceId}
                  containerWidth={
                    previewDevice === 'mobile'
                      ? '375px'
                      : previewDevice === 'tablet'
                        ? '768px'
                        : templateStyles.containerWidth || '600px'
                  }
                />
              </div> {/* Closes mock browser chrome outer div */}
            </div>

            {/* ── Layers button + panel ─────────────────────────────────── */}
            {!isPreviewMode && (
              <div className="absolute bottom-4 left-4 z-[250]">
                {showLayersPanel && (
                  <LayersPanel
                    blocks={blocks}
                    selectedBlockId={selectedBlockId}
                    onSelectBlock={(id) => { handleBlockSelect(id); }}
                    onReorderBlocks={(reordered) => setBlocks(reordered)}
                    onDeleteBlock={(id) => {
                      setBlocks(prev => prev.filter(b => b.id !== id));
                      if (selectedBlockId === id) setSelectedBlockId(null);
                    }}
                    onDuplicateBlock={(id) => {
                      const block = blocks.find(b => b.id === id);
                      if (block) {
                        const clone = { ...JSON.parse(JSON.stringify(block)), id: `${block.type}-${Date.now()}` };
                        const idx = blocks.findIndex(b => b.id === id);
                        setBlocks(prev => [...prev.slice(0, idx + 1), clone, ...prev.slice(idx + 1)]);
                      }
                    }}
                    onClose={() => setShowLayersPanel(false)}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setShowLayersPanel(v => !v)}
                  title="Manage Layers"
                  className={`flex items-center justify-center w-9 h-9 rounded-xl shadow-lg transition-all duration-150 ${
                    showLayersPanel
                      ? 'bg-slate-900 text-white shadow-xl'
                      : 'bg-white border border-black/[0.09] text-slate-600 hover:text-slate-900 hover:shadow-xl hover:scale-[1.05]'
                  }`}
                >
                  {/* Layers / stack icon */}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3" />
                  </svg>
                </button>
              </div>
            )}
          </main>

          {/* ── Floating Add Panel (Wix-style) ──────────────────────────────── */}
          {isAddPanelOpen && !isPreviewMode && (
            <>
              {/* Backdrop — clicking outside closes both picker and panel */}
              <div
                className="fixed inset-0 z-[45]"
                onClick={() => { setIsAddPanelOpen(false); setAddPanelCategory(null); }}
              />
              {/* Step 2: Floating BlockPalette panel — popover anchored top-left */}
              {addPanelCategory !== null && (
                <div
                  className="fixed z-[46] flex flex-col rounded-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                  style={{
                    top: '108px',
                    left: '16px',
                    width: '580px',
                    height: 'calc(100vh - 128px)',
                    background: 'rgba(255,255,255,0.98)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.10), 0 32px 80px rgba(0,0,0,0.18)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Slim panel header — just the title + close; category tabs live inside BlockPalette */}
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-black/[0.06] flex-shrink-0 bg-white">
                    <span className="text-[13px] font-semibold text-slate-700 tracking-[-0.01em]">Add to email</span>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setIsAddPanelOpen(false); setAddPanelCategory(null); }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-black/[0.06] transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* BlockPalette fills the rest — icon rail hidden, tab strip shown inside */}
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <BlockPalette
                      selectedBlock={panelBlock}
                      onBlockUpdate={handlePanelBlockUpdate}
                      onUpdateBlocks={(newBlocks) => setBlocks(newBlocks)}
                      onSelectBlock={(id) => {
                        handleBlockSelect(id);
                        setIsAddPanelOpen(false);
                        setAddPanelCategory(null);
                      }}
                      onDeselectBlock={() => {
                        setSelectedBlockId(null);
                        setSelectedColumnBlock(null);
                      }}
                      allBlocks={blocks}
                      onClose={() => { setIsAddPanelOpen(false); setAddPanelCategory(null); }}
                      initialCategory={addPanelCategory}
                      hideIconRail
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DndContext >

      {/* Right Side Properties Panel — always reserved, shows content when block selected */}
      {!isPreviewMode && (
        <aside className="fixed right-0 top-[57px] bottom-0 w-[320px] flex flex-col z-40" style={{ borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
          {panelBlock && !panelBlock.isScubamailBranding ? (
            <div className="flex-1 overflow-y-auto overflow-x-visible no-scrollbar hide-scrollbar animate-in slide-in-from-right duration-200">
              <PropertiesPanel
                block={panelBlock!}
                onUpdate={handlePanelBlockUpdate}
                onDone={() => setSelectedBlockId(null)}
                allBlocks={blocks}
              />
            </div>
          ) : (
            /* Empty state — block not selected */
            <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6">
              <div className="w-10 h-10 rounded-xl bg-black/[0.04] flex items-center justify-center">
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-[12px] font-medium text-slate-500">No block selected</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-snug">Click a block on the canvas to edit its properties</p>
              </div>
            </div>
          )}
        </aside>
      )}

      {/* Preview Modal */}
      {
        isPreviewModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-end"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsPreviewModalOpen(false);
              }
            }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              style={{
                animation: 'fadeIn 0.3s ease-out',
              }}
            />

            {/* Modal Content - Slides up from bottom */}
            <div
              className="relative w-full h-[90vh] bg-white rounded-t-2xl shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              style={{
                animation: 'slideUp 0.3s ease-out',
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
                <button
                  onClick={() => setIsPreviewModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Navigation Tabs */}
              <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200">
                <button
                  onClick={() => setPreviewViewMode('desktop')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${previewViewMode === 'desktop'
                    ? 'bg-teal-50 text-teal-700 border-2 border-teal-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  Desktop
                </button>
                <button
                  onClick={() => setPreviewViewMode('tablet')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${previewViewMode === 'tablet'
                    ? 'bg-teal-50 text-teal-700 border-2 border-teal-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  Tablet
                </button>
                <button
                  onClick={() => setPreviewViewMode('mobile')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${previewViewMode === 'mobile'
                    ? 'bg-teal-50 text-teal-700 border-2 border-teal-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  Mobile
                </button>

                {/* Dark mode preview toggle */}
                <button
                  onClick={() => setIsDarkModePreview(prev => !prev)}
                  title="Toggle dark mode preview"
                  className={`ml-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${isDarkModePreview ? 'bg-slate-800 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                  Dark
                </button>

                <button
                  onClick={() => setShowTestEmailModal(true)}
                  className="ml-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center space-x-2 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                  <span>Send a Test Email</span>
                </button>

                <div className="ml-auto flex items-center gap-1.5">
                  <input
                    type="text"
                    readOnly
                    title={previewShareUrl || undefined}
                    value={previewShareLoading ? 'Generating…' : previewShareUrl || 'No preview link'}
                    className="w-56 sm:w-72 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 truncate"
                  />
                  <button
                    type="button"
                    onClick={handleCopyPreviewLink}
                    disabled={!previewShareUrl || previewShareLoading}
                    title="Copy preview link"
                    className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {previewLinkCopied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenPreviewInBrowser}
                    disabled={!previewShareUrl || previewShareLoading}
                    title="Open preview in browser"
                    className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Open in browser
                  </button>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 flex overflow-hidden">
                {/* Email Preview Area */}
                <div className="flex-1 overflow-auto p-6 bg-gray-50">
                  <div className="mx-auto" style={{ maxWidth: '100%' }}>
                    <div
                      className="mx-auto transition-all duration-300 ease-in-out relative flex flex-col items-center"
                      style={{
                        width: previewViewMode === 'mobile' ? '375px' : previewViewMode === 'tablet' ? '768px' : '100%',
                        maxWidth: '100%',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden',
                        minHeight: '600px',
                        marginBottom: '40px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                      }}
                    >
                      <iframe
                        key={previewViewMode}
                        srcDoc={buildPreviewHtml()}
                        className="w-full border-0"
                        style={{
                          height: 'calc(90vh - 180px)',
                          width: '100%',
                          minHeight: '600px',
                        }}
                        title="Email Preview"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Test Email</h3>

            {isTestEmailSent ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-gray-900">Email Sent!</h4>
                <p className="text-gray-600 mt-2 mb-6">
                  Your test email has been sent to <br />
                  <span className="font-semibold text-gray-800">{testEmailAddress}</span>
                </p>
                <button
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setIsTestEmailSent(false);
                    setTestEmailAddress('');
                  }}
                  className="px-6 py-2 bg-slate-900 text-white font-medium rounded-lg hover:bg-black transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Sender Identity Selection */}
              {(verifiedEmails.length > 0 || verifiedDomains.length > 0) ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={testFromName}
                      onChange={(e) => setTestFromName(e.target.value)}
                      placeholder="Sender Name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Email
                    </label>
                    <select
                      value={testFromEmail}
                      onChange={(e) => setTestFromEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                      <option value="" disabled>Select a verified sender</option>
                      {verifiedEmails.map(v => (
                        <option key={v.id} value={v.email}>{v.email} (Verified Email)</option>
                      ))}
                      {verifiedDomains.map(d => (
                        <option key={d.id} value={`info@${d.domain}`}>info@{d.domain} (Verified Domain)</option>
                      ))}
                    </select>
                    {actualSendingEmail && (
                      <p className="mt-1 text-xs text-gray-500 italic">
                        Sends as: <span className="font-medium text-gray-700">{actualSendingEmail}</span>
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex gap-3">
                    <svg className="w-5 h-5 text-amber-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-semibold text-amber-800">Sender Identity Required</h4>
                      <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                        You must verify an email address or domain in your workspace settings before you can send test emails.
                      </p>
                      <button
                        onClick={() => navigate(`/workspace/${effectiveWorkspaceId}/settings/senders`)}
                        className="mt-3 text-xs font-semibold text-amber-800 hover:text-amber-900 flex items-center gap-1 group"
                      >
                        Go to Sender Authentication
                        <svg className="w-3 h-3 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                  disabled={verifiedEmails.length === 0 && verifiedDomains.length === 0}
                  autoFocus
                />
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowTestEmailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isSendingTestEmail}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!testEmailAddress.trim()) {
                      showError('Please enter a valid email address');
                      return;
                    }

                    if (!testFromEmail) {
                      showError('Please select a verified sender email');
                      return;
                    }

                    try {
                      setIsSendingTestEmail(true);

                      const htmlContent = generateEmailHTML(blocks, templateStyles, {
                        previewText: template?.previewText,
                        pollTracking: effectiveWorkspaceId && campaignId ? {
                          workspaceId: effectiveWorkspaceId,
                          campaignId: campaignId
                        } : undefined,
                        branding: brandingOptions,
                        address: formattedAddress,
                        hideBranding: effectiveCanRemoveBranding
                      });

                      const result = await EmailService.sendEmail({
                        to: testEmailAddress,
                        subject: `[Test] ${templateSubject}`,
                        htmlBody: htmlContent,
                        textBody: htmlContent.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
                        fromEmail: testFromEmail,
                        fromName: testFromName || currentWorkspace?.name || 'Scubamail Builder',
                        userId: currentUser?.uid,
                        workspaceId: effectiveWorkspaceId
                      });

                      if (result.success) {
                        showSuccess(`Test email sent to ${testEmailAddress}`);
                        setIsTestEmailSent(true);
                        // No auto-close, wait for user to click close
                      } else {
                        showError(result.error || 'Failed to send test email');
                      }
                    } catch (error) {
                      console.error('Failed to send test email:', error);
                      showError('An error occurred while sending the test email');
                    } finally {
                      setIsSendingTestEmail(false);
                    }
                  }}
                  disabled={isSendingTestEmail || (verifiedEmails.length === 0 && verifiedDomains.length === 0)}
                  className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-black rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm min-w-[120px]"
                >
                  {isSendingTestEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Send Email</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}


      {/* AI Email Generator Modal */}
      <AIEmailGenerator
        isOpen={isAIGeneratorOpen}
        onClose={() => setIsAIGeneratorOpen(false)}
        onGenerate={handleAIGenerate}
        campaignName={campaignName}
        senderName={template?.name || 'Scubamail'}
      />

      {/* Keyboard Shortcuts Modal */}
      {
        isShortcutsModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            onClick={() => setIsShortcutsModalOpen(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Keyboard Shortcuts</h2>
                <button
                  onClick={() => setIsShortcutsModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Block Actions</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Delete selected block</span>
                      <div className="flex items-center gap-1">
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">Delete</kbd>
                        <span className="text-slate-400 text-xs">or</span>
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">Backspace</kbd>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Deselect block</span>
                      <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">Esc</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Select / Clear all blocks</span>
                      <div className="flex items-center gap-1">
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">⌘</kbd>
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">A</kbd>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-px bg-slate-100" />
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">History</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Undo</span>
                      <div className="flex items-center gap-1">
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">⌘</kbd>
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">Z</kbd>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Redo</span>
                      <div className="flex items-center gap-1">
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">⌘</kbd>
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">⇧</kbd>
                        <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">Z</kbd>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-px bg-slate-100" />
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Context Menu</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">Open block actions</span>
                      <kbd className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">Right-click</kbd>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                <p className="text-xs text-slate-500 text-center">Tip: Select a block first to use block actions</p>
              </div>
            </div>
          </div>
        )
      }

      {/* Import Confirmation Modal */}
      {showImportConfirmModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[slideUp_0.3s_ease-out]">
            <div className="px-6 py-6 text-center">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Replace Current Design?</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Importing this template will <span className="font-semibold text-slate-900 text-sm">permanently replace</span> your current design. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
              <button
                onClick={() => {
                  setShowImportConfirmModal(false);
                  setImportPendingData(null);
                }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmImportTemplate}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-xl shadow-lg shadow-slate-900/20 transition-all active:scale-[0.98]"
              >
                Yes, Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
    </CanvaImportGuideProvider>
    </>
  );
};

export default ModernEmailBuilder;




